import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Save, Printer, ChevronDown, ChevronUp, Calendar, Check, Search, AlertTriangle, ChevronsUpDown, Scissors, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import ReelSuggestionDialog from "@/components/ReelSuggestionDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkOrderCreationNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const cloneData = location.state?.cloneData;
  const editData = location.state?.editData;
  const isEditMode = !!editData;
  const editWorkOrderId = editData?.id || null;
  
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerForHeader, setSelectedCustomerForHeader] = useState("");
  const [selectedCustomerItems, setSelectedCustomerItems] = useState([]);
  const [availableInventory, setAvailableInventory] = useState([]);
  const [woItems, setWoItems] = useState([]);
  const [itemNameOpen, setItemNameOpen] = useState(false);
  
  // Product Types from Store Database (for P3 Top Paper Type)
  const [productTypes, setProductTypes] = useState([]);
  const [itemNameSearch, setItemNameSearch] = useState("");
  
  // Product Specifications state for Product ID dropdown
  const [productSpecs, setProductSpecs] = useState([]);
  const [productIdOpen, setProductIdOpen] = useState(false);
  const [productIdSearch, setProductIdSearch] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  // Reel-suggestion dialog state (opens after Product pick + via manual button in P1)
  const [reelSuggestionOpen, setReelSuggestionOpen] = useState(false);
  const [reelSuggestionSpec, setReelSuggestionSpec] = useState(null);
  
  const [expandedProcesses, setExpandedProcesses] = useState({
    process1: true,
    process2a: false,  // Sheet Cutting
    process2b: false,  // Paper Cutting
    process3: false,
    process4: false,
    process5: false,
    process6: false,
    process6a: false,  // Eccentric Slotter (added Feb 2026)
    process7: false,
    process8: false,
    process9: false,
    process9a: false,  // Board Drying
    process10: false,
    process11: false,
    process12: false,
  });

  // Process Selection - Process 11 & 12 are mandatory and always selected
  const [selectedProcesses, setSelectedProcesses] = useState({
    process1: true,
    process2a: true,   // Sheet Cutting
    process2b: true,   // Paper Cutting
    process3: true,
    process4: true,
    process5: true,
    process6: true,
    process6a: false,  // Eccentric Slotter — opt-in like Board Drying
    process7: true,
    process8: true,
    process9: true,
    process9a: false,  // Board Drying
    process10: true,
    process11: true,  // Mandatory - always selected
    process12: true,  // Mandatory - always selected
  });

  // Track the order in which processes are selected (for dynamic rendering)
  const [processOrder, setProcessOrder] = useState([
    'process1', 'process2a', 'process2b', 'process3', 'process4',
    'process5', 'process6', 'process6a', 'process7', 'process8', 'process9',
    'process10', 'process11', 'process12'
  ]);

  // Header Data
  const [headerData, setHeaderData] = useState({
    customer_name: "",
    delivery_date: "",
    requested_by: "",
    required_quantity: "",
    item_name: "",
    reel_size: "",
    cutting_size: "",
    order_received_date: "",
    work_order_number: "",
    work_order_creation_date: new Date().toISOString().split('T')[0],
    po_number: "",
    spd_order_id: "",
    dimension_unit: "mm",
    length: "",
    length_mm: "",
    breadth: "",
    breadth_mm: "",
    height: "",
    height_mm: "",
    ups: "",
    joint_type: "",
    ply_type: "",
    item_rate: "",  // Price per item from Product & Material Info
    product_id: "", // Product ID reference
  });

  // UPs List with ability to add custom values
  const [upsList, setUpsList] = useState(['1 UP', '2 UP', '3 UP', '4 UP', '5 UP', '6 UP', '8 UP', '10 UP', '12 UP']);
  const [showAddUp, setShowAddUp] = useState(false);
  const [newUpValue, setNewUpValue] = useState("");

  // Paper Count hint for P2B (calculated based on ply)
  const [paperCountHint, setPaperCountHint] = useState(0);
  
  // Pasting Board Count hint for P3 (same as paper count)
  const [pastingBoardHint, setPastingBoardHint] = useState(0);

  // SPD Orders (Received Purchase Orders) for PO dropdown
  const [spdOrders, setSpdOrders] = useState([]);
  const [poDropdownOpen, setPoDropdownOpen] = useState(false);
  const [loadingSpdOrders, setLoadingSpdOrders] = useState(false);
  const [selectedSpdOrder, setSelectedSpdOrder] = useState(null);

  // Process 1 - Corrugation (Dynamic Rows)
  const [process1, setProcess1] = useState({
    date: "",
    flute_type: "Narrow Flute",
    rows: [{ base_reel_id: "", base_weight: "", flute_reel_id: "", flute_weight: "", baseOpen: false, fluteOpen: false }]
  });

  // Process 2A - Sheet Cutting (separated from Paper Cutting)
  const [process2a, setProcess2a] = useState({
    date: "",
    reels: [{ reel_no: "", reel_size: "", reel_weight: "", reel_product: "" }],
    cutting_size: "", sheet_counting: "",
    use_p1_output: false,
  });
  const [p2aReelOpen, setP2aReelOpen] = useState({});
  const [p2aSearchTerm, setP2aSearchTerm] = useState("");

  // Flute types for P1→P2A sheet calculation
  const [fluteTypes, setFluteTypes] = useState([]);
  const [p1SheetHint, setP1SheetHint] = useState(null);

  // Process 2B - Paper Cutting (separated from Sheet Cutting)
  const [process2b, setProcess2b] = useState({
    date: "",
    reels: [{ reel_no: "", reel_size: "", reel_weight: "", reel_product: "" }],
    cutting_size: "", paper_counting: "", top_paper_weight: ""
  });
  const [p2bReelOpen, setP2bReelOpen] = useState({});
  const [p2bSearchTerm, setP2bSearchTerm] = useState("");

  // Process 3 - Sheet Pasting
  const [process3, setProcess3] = useState({
    date: "",
    no_of_ply: "3", 
    top_paper_type: "", 
    stock_id: "", 
    reel_size: "", 
    cutting_size: "", 
    sheet_counting: "",
    use_p2b_output: false,  // User manually enables if they want to use P2B paper
  });
  const [p3StockOpen, setP3StockOpen] = useState(false);
  const [p3SearchTerm, setP3SearchTerm] = useState("");

  // Process 4 - Creasing
  const [process4, setProcess4] = useState({
    date: "",
    length: "", breadth: "", height: "", reel_size: "", cutting_size: "", board_counting: ""
  });

  // Process 5 - RS4
  const [process5, setProcess5] = useState({
    date: "",
    length: "", breadth: "", height: "", reel_size: "", cutting_size: "",
    printing: "no", color: "", board_counting: "",
    corrugated_roll_id: "", corrugated_roll_size: "", corrugated_roll_weight: ""
  });

  // Process 6 - RS4 Single Color Printing & Slotting
  const [process6, setProcess6] = useState({
    date: "",
    length: "", breadth: "", height: "", reel_size: "", cutting_size: "",
    printing: "No", color: "", board_counting: ""
  });

  // Process 6A - Eccentric Slotter (added Feb 2026, slotted between P6 and P7)
  const [process6a, setProcess6a] = useState({
    date: "",
    length: "", breadth: "", height: "",
    board_counting: ""
  });

  // Process 7 - Flatbed Punching
  const [process7, setProcess7] = useState({
    date: "",
    item_name: "", length: "", breadth: "", height: "", reel_size: "", cutting_size: "",
    die_id: "", die_ids: [], die_countings: {}, number_of_dies: "1", counting: ""
  });

  // Process 8 - Rotary Die Punching
  const [process8, setProcess8] = useState({
    date: "",
    item_name: "", length: "", breadth: "", height: "", reel_size: "", cutting_size: "",
    die_id: "", die_ids: [], die_countings: {}, printing: "no", color: "", counting: ""
  });

  // Process 9 - Calico Pasting
  const [process9, setProcess9] = useState({
    date: "",
    calico_color: "", number_of_calico: ""
  });

  // Process 9A - Board Drying (labor cost only, no material tracking)
  const [process9a, setProcess9a] = useState({
    date: "",
    notes: ""
  });

  const [process10, setProcess10] = useState({
    date: "",
    boxes_in_bundle: "",
    stitching_type: "Single",
    total_bundles: "",
    total_boxes: ""
  });

  // Process 11 - Loading / Unloading
  const [process11, setProcess11] = useState({
    date: "",
    loading_type: "Loading",
    vehicle_number: "",
    remarks: ""
  });

  // Process 12 - Transport
  const [process12, setProcess12] = useState({
    date: "",
    transport_type: "Internal",
    destination: "",
    transport_remarks: ""
  });

  // Used Reel Warning State
  const [usedReelWarning, setUsedReelWarning] = useState({
    open: false,
    reelId: "",
    workOrders: [],
    callback: null,
    type: "", // 'base', 'flute', or 'p2'
    index: null
  });

  // Reel Split Tracking - when selected reel is larger than item's required size
  const [reelSplitInfo, setReelSplitInfo] = useState({
    has_split: false,
    remaining_size: 0,
    stock_reel_size: 0,
    item_reel_size: 0,
    handling: "", // "assign_wo" | "corrugated_roll_stock" | "sheet_stock"
    assigned_wo: "",
    notes: ""
  });

  // Available work orders for reel split assignment
  const [availableWoList, setAvailableWoList] = useState([]);
  const [woSplitDropdownOpen, setWoSplitDropdownOpen] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchAvailableInventory();
    fetchWoItems();
    fetchProductSpecs();
    fetchProductTypes();
    fetchFluteTypes();
    
    // Initialize from clone or edit data if available
    const initData = editData || cloneData;
    if (initData) {
      if (editData) toast.info("Editing work order - modify values and save");
      if (cloneData) toast.info("Cloning work order - modify values as needed");
      
      if (initData.header) {
        setHeaderData(initData.header);
        setSelectedCustomerForHeader(initData.header.customer_name || "");
      }
      
      if (initData.processes) {
        if (initData.processes.process1) {
          const p1 = initData.processes.process1;
          const rows = (p1.rows || []).map(row => ({
            base_reel_id: row.base_reel_id || "",
            base_weight: row.base_weight || "",
            flute_reel_id: row.flute_reel_id || "",
            flute_weight: row.flute_weight || "",
            baseOpen: false,
            fluteOpen: false
          }));
          setProcess1({
            ...p1,
            rows: rows.length > 0 ? rows : [{ base_reel_id: "", base_weight: "", flute_reel_id: "", flute_weight: "", baseOpen: false, fluteOpen: false }]
          });
        }
        if (initData.processes.process2a) {
          const p2a = initData.processes.process2a;
          if (p2a.reels && Array.isArray(p2a.reels)) {
            setProcess2a(p2a);
          } else if (p2a.reel_no) {
            setProcess2a({
              date: p2a.date || "",
              reels: [{ reel_no: p2a.reel_no, reel_size: p2a.reel_size || "", reel_weight: p2a.reel_weight || "" }],
              cutting_size: p2a.cutting_size || "",
              sheet_counting: p2a.sheet_counting || ""
            });
          } else {
            setProcess2a(prev => ({ ...prev, ...p2a }));
          }
        }
        if (initData.processes.process2b) {
          const p2b = initData.processes.process2b;
          if (p2b.reels && Array.isArray(p2b.reels)) {
            setProcess2b(p2b);
          } else if (p2b.reel_no) {
            setProcess2b({
              date: p2b.date || "",
              reels: [{ reel_no: p2b.reel_no, reel_size: p2b.reel_size || "", reel_weight: p2b.reel_weight || "" }],
              cutting_size: p2b.cutting_size || "",
              paper_counting: p2b.paper_counting || ""
            });
          } else {
            setProcess2b(prev => ({ ...prev, ...p2b }));
          }
        }
        if (initData.processes.process2 && !initData.processes.process2a && !initData.processes.process2b) {
          const p2 = initData.processes.process2;
          if (p2.reels && Array.isArray(p2.reels)) {
            setProcess2a({ date: p2.date || "", reels: p2.reels, cutting_size: p2.cutting_size || "", sheet_counting: p2.sheet_counting || "" });
          } else if (p2.reel_no) {
            setProcess2a({ date: p2.date || "", reels: [{ reel_no: p2.reel_no, reel_size: p2.reel_size || "", reel_weight: p2.reel_weight || "" }], cutting_size: p2.cutting_size || "", sheet_counting: p2.sheet_counting || "" });
          }
          setProcess2b({ date: p2.date || "", reels: [{ reel_no: "", reel_size: "", reel_weight: "", reel_product: "" }], cutting_size: p2.cutting_size || "", paper_counting: p2.paper_counting || "" });
        }
        if (initData.processes.process3) setProcess3(initData.processes.process3);
        if (initData.processes.process4) setProcess4(initData.processes.process4);
        if (initData.processes.process5) setProcess5(initData.processes.process5);
        if (initData.processes.process6) setProcess6(initData.processes.process6);
        if (initData.processes.process6a) setProcess6a(initData.processes.process6a);
        if (initData.processes.process7) setProcess7(initData.processes.process7);
        if (initData.processes.process8) setProcess8(initData.processes.process8);
        if (initData.processes.process9) setProcess9(initData.processes.process9);
        if (initData.processes.process9a) setProcess9a(initData.processes.process9a);
        if (initData.processes.process10) setProcess10(initData.processes.process10);
        if (initData.processes.process11) setProcess11(initData.processes.process11);
        if (initData.processes.process12) setProcess12(initData.processes.process12);
      }
      
      if (initData.selected_processes) {
        setSelectedProcesses({
          ...initData.selected_processes,
          process11: true,
          process12: true,
        });
      }
    } else {
      fetchLastWONumber();
    }
  }, []);

  // Auto-convert dimensions between inches and mm
  useEffect(() => {
    if (headerData.dimension_unit === "inches") {
      // User is entering in inches, convert to mm
      if (headerData.length && !isNaN(parseFloat(headerData.length))) {
        const newMm = (parseFloat(headerData.length) * 25.4).toFixed(2);
        if (headerData.length_mm !== newMm) {
          setHeaderData(prev => ({ ...prev, length_mm: newMm }));
        }
      }
      if (headerData.breadth && !isNaN(parseFloat(headerData.breadth))) {
        const newMm = (parseFloat(headerData.breadth) * 25.4).toFixed(2);
        if (headerData.breadth_mm !== newMm) {
          setHeaderData(prev => ({ ...prev, breadth_mm: newMm }));
        }
      }
      if (headerData.height && !isNaN(parseFloat(headerData.height))) {
        const newMm = (parseFloat(headerData.height) * 25.4).toFixed(2);
        if (headerData.height_mm !== newMm) {
          setHeaderData(prev => ({ ...prev, height_mm: newMm }));
        }
      }
    } else {
      // User is entering in mm, convert to inches
      if (headerData.length_mm && !isNaN(parseFloat(headerData.length_mm))) {
        const newIn = (parseFloat(headerData.length_mm) / 25.4).toFixed(2);
        if (headerData.length !== newIn) {
          setHeaderData(prev => ({ ...prev, length: newIn }));
        }
      }
      if (headerData.breadth_mm && !isNaN(parseFloat(headerData.breadth_mm))) {
        const newIn = (parseFloat(headerData.breadth_mm) / 25.4).toFixed(2);
        if (headerData.breadth !== newIn) {
          setHeaderData(prev => ({ ...prev, breadth: newIn }));
        }
      }
      if (headerData.height_mm && !isNaN(parseFloat(headerData.height_mm))) {
        const newIn = (parseFloat(headerData.height_mm) / 25.4).toFixed(2);
        if (headerData.height !== newIn) {
          setHeaderData(prev => ({ ...prev, height: newIn }));
        }
      }
    }
  }, [headerData.dimension_unit, headerData.length, headerData.breadth, headerData.height, headerData.length_mm, headerData.breadth_mm, headerData.height_mm]);

  // Calculate Paper Count Hint based on Ply and Sheet Counting (P2A)
  // Formula: 3 ply = sheets × 1, 5 ply = sheets ÷ 2, 7 ply = sheets ÷ 3
  useEffect(() => {
    const sheetCount = parseInt(process2a.sheet_counting) || 0;
    const ply = parseInt(process3.no_of_ply) || 3;
    
    let calculatedPaperCount = 0;
    if (sheetCount > 0) {
      if (ply === 3) {
        calculatedPaperCount = sheetCount;
      } else if (ply === 5) {
        calculatedPaperCount = Math.floor(sheetCount / 2);
      } else if (ply === 7) {
        calculatedPaperCount = Math.floor(sheetCount / 3);
      } else if (ply === 9) {
        calculatedPaperCount = Math.floor(sheetCount / 4);
      } else if (ply === 11) {
        calculatedPaperCount = Math.floor(sheetCount / 5);
      } else {
        calculatedPaperCount = sheetCount;
      }
    }
    setPaperCountHint(calculatedPaperCount);
    setPastingBoardHint(calculatedPaperCount);
  }, [process2a.sheet_counting, process3.no_of_ply]);

  // Auto-populate P4, P7, P8 board/counting = P3 sheet_counting × UPS
  useEffect(() => {
    const pastingCount = parseInt(process3.sheet_counting) || 0;
    const upsVal = parseInt(String(headerData.ups).replace(/\D/g, '')) || 1;
    if (pastingCount > 0 && upsVal > 0) {
      const multiplied = pastingCount * upsVal;
      setProcess4(prev => ({ ...prev, board_counting: multiplied.toString() }));
      setProcess7(prev => ({ ...prev, counting: multiplied.toString() }));
      setProcess8(prev => ({ ...prev, counting: multiplied.toString() }));
    }
  }, [process3.sheet_counting, headerData.ups]);

  // Calculate Total Boxes for P10 (boxes_in_bundle × total_bundles)
  const totalBoxesCalculated = React.useMemo(() => {
    const boxesInBundle = parseInt(process10.boxes_in_bundle) || 0;
    const totalBundles = parseInt(process10.total_bundles) || 0;
    return (boxesInBundle * totalBundles).toString();
  }, [process10.boxes_in_bundle, process10.total_bundles]);

  // Reel Split Detection - compare corrugated roll size with item reel size
  // Triggers when: P2A has a roll selected OR P5 has a corrugated roll selected
  useEffect(() => {
    const itemReelSize = parseFloat(headerData.reel_size) || 0;
    if (itemReelSize <= 0) {
      if (reelSplitInfo.has_split) setReelSplitInfo(prev => ({ ...prev, has_split: false, remaining_size: 0 }));
      return;
    }

    let stockRollSize = 0;
    let splitSource = "";

    // Check P5 corrugated roll first (direct roll selection in P5)
    if (selectedProcesses.process5 && process5.corrugated_roll_id) {
      const p5RollInv = availableInventory.find(inv => inv.reel_id === process5.corrugated_roll_id);
      stockRollSize = parseFloat(p5RollInv?.reel_size || process5.corrugated_roll_size) || 0;
      splitSource = "P5";
    }
    // Fallback: Check P2A corrugated roll (when P2A feeds into P5)
    else if (selectedProcesses.process2a && selectedProcesses.process5) {
      const firstReel = process2a.reels?.[0];
      if (firstReel?.reel_no) {
        const rollInv = availableInventory.find(inv => inv.reel_id === firstReel.reel_no);
        stockRollSize = parseFloat(rollInv?.reel_size || firstReel.reel_size) || 0;
        splitSource = "P2A";
      }
    }

    if (stockRollSize > itemReelSize) {
      const remaining = parseFloat((stockRollSize - itemReelSize).toFixed(2));
      setReelSplitInfo(prev => ({
        ...prev,
        has_split: true,
        remaining_size: remaining,
        stock_reel_size: stockRollSize,
        item_reel_size: itemReelSize,
        split_source: splitSource
      }));
    } else {
      if (reelSplitInfo.has_split) {
        setReelSplitInfo(prev => ({ ...prev, has_split: false, remaining_size: 0 }));
      }
    }
  }, [headerData.reel_size, process2a.reels, process5.corrugated_roll_id, process5.corrugated_roll_size, availableInventory, selectedProcesses.process2a, selectedProcesses.process5]);

  // Fetch available work orders for reel split assignment
  useEffect(() => {
    if (reelSplitInfo.has_split && reelSplitInfo.handling === "assign_existing_wo") {
      axios.get(`${API}/work-orders`).then(res => {
        const wos = (res.data || []).filter(wo => 
          wo.status === 'available' || wo.status === 'in_progress' || wo.status === 'draft'
        ).map(wo => ({
          number: wo.header?.work_order_number,
          customer: wo.header?.customer_name,
          item: wo.header?.item_name,
          reel_size: wo.header?.reel_size
        }));
        setAvailableWoList(wos);
      }).catch(() => {});
    }
  }, [reelSplitInfo.has_split, reelSplitInfo.handling]);

  // Create Draft Work Order for reel split
  const createDraftWorkOrder = async () => {
    if (!reelSplitInfo.has_split || reelSplitInfo.remaining_size <= 0) return;
    
    try {
      // Get next WO number
      const woRes = await axios.get(`${API}/work-orders`);
      const allWos = woRes.data || [];
      const maxNum = allWos.reduce((max, wo) => {
        const num = parseInt(wo.header?.work_order_number?.replace('WONO-', '') || '0');
        return num > max ? num : max;
      }, 0);
      const nextWoNum = `WONO-${String(maxNum + 1).padStart(3, '0')}`;

      const draftWo = {
        header: {
          customer_name: headerData.customer_name || "",
          delivery_date: "",
          requested_by: "",
          required_quantity: "",
          item_name: headerData.item_name || "",
          reel_size: reelSplitInfo.remaining_size.toString(),
          cutting_size: headerData.cutting_size || "",
          order_received_date: "",
          work_order_number: nextWoNum,
          work_order_creation_date: new Date().toISOString().split('T')[0],
          po_number: "",
          spd_order_id: "",
          dimension_unit: headerData.dimension_unit || "mm",
          length: headerData.length || "",
          breadth: headerData.breadth || "",
          height: headerData.height || "",
          length_mm: headerData.length_mm || "",
          breadth_mm: headerData.breadth_mm || "",
          height_mm: headerData.height_mm || "",
          ups: headerData.ups || "",
          joint_type: headerData.joint_type || "",
          item_rate: headerData.item_rate || "",
          product_id: headerData.product_id || "",
          draft_source: headerData.work_order_number || "",
          draft_reason: `Reel split from ${headerData.work_order_number} - remaining ${reelSplitInfo.remaining_size}" of ${reelSplitInfo.stock_reel_size}" roll`
        },
        processes: {},
        selected_processes: {},
        status: "draft"
      };

      const res = await axios.post(`${API}/work-orders`, draftWo);
      const createdNum = res.data?.header?.work_order_number || nextWoNum;
      
      setReelSplitInfo(prev => ({ 
        ...prev, 
        handling: "new_draft_wo", 
        assigned_wo: createdNum 
      }));
      
      toast.success(`Draft Work Order ${createdNum} created with reel size ${reelSplitInfo.remaining_size}" - Data pending`);
    } catch (error) {
      console.error("Error creating draft WO:", error);
      toast.error("Failed to create draft work order");
    }
  };

  // Auto-populate processes with header data
  useEffect(() => {
    const autoData = {
      reel_size: headerData.reel_size,
      cutting_size: headerData.cutting_size,
      length: headerData.length_mm,
      breadth: headerData.breadth_mm,
      height: headerData.height_mm,
      item_name: headerData.item_name,
    };

    // Update process2a and process2b with auto-filled data
    setProcess2a(prev => ({ ...prev, cutting_size: autoData.cutting_size }));
    setProcess2b(prev => ({ ...prev, cutting_size: autoData.cutting_size }));
    setProcess3(prev => ({ ...prev, reel_size: autoData.reel_size, cutting_size: autoData.cutting_size }));
    setProcess4(prev => ({ ...prev, ...autoData }));
    setProcess5(prev => ({ ...prev, ...autoData }));
    setProcess6(prev => ({ ...prev, ...autoData }));
    // P6A (Eccentric Slotter): only L/B/H need auto-fill — no reel/cutting on this machine.
    setProcess6a(prev => ({ ...prev, length: autoData.length, breadth: autoData.breadth, height: autoData.height }));
    setProcess7(prev => ({ ...prev, ...autoData }));
    setProcess8(prev => ({ ...prev, ...autoData }));
  }, [headerData]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/wo-customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchAvailableInventory = async () => {
    try {
      // Fetch ALL inventory (not just available) so users can re-use stock_used items with warning
      const response = await axios.get(`${API}/inventory`);
      setAvailableInventory(response.data);
    } catch (error) {
      console.error("Error fetching inventory:", error);
    }
  };

  const fetchWoItems = async () => {
    try {
      const response = await axios.get(`${API}/wo-items`);
      setWoItems(response.data || []);
    } catch (error) {
      console.error("Error fetching WO items:", error);
    }
  };

  const fetchProductSpecs = async () => {
    try {
      const response = await axios.get(`${API}/product-specifications`);
      setProductSpecs(response.data || []);
    } catch (error) {
      console.error("Error fetching product specifications:", error);
    }
  };

  // Fetch Product Types from Store Database
  const fetchProductTypes = async () => {
    try {
      const response = await axios.get(`${API}/product-types`);
      setProductTypes(response.data || []);
    } catch (error) {
      console.error("Error fetching product types:", error);
    }
  };

  // Fetch Flute Types with multipliers
  const fetchFluteTypes = async () => {
    try {
      const response = await axios.get(`${API}/flute-types`);
      setFluteTypes(response.data || []);
    } catch (error) {
      console.error("Error fetching flute types:", error);
    }
  };

  // P2A - use P1 output state
  const [p1ReelData, setP1ReelData] = useState({ baseReels: [], fluteReels: [] });

  // Extract GSM from rm_name pattern (e.g., NS18018 → 180, GYT12020 → 120)
  const extractGSMFromRmName = (rmName) => {
    if (!rmName) return 0;
    const match = rmName.match(/[A-Za-z]+(\d{3})\d{2}/);
    return match ? parseInt(match[1]) : 0;
  };

  // Fetch reel data from inventory for P1 rows
  useEffect(() => {
    if (!process2a.use_p1_output || !selectedProcesses.process1) {
      setP1ReelData({ baseReels: [], fluteReels: [] });
      return;
    }
    const p1Rows = process1.rows || [];
    const baseReels = [];
    const fluteReels = [];
    p1Rows.forEach(row => {
      if (row.base_reel_id) {
        const inv = availableInventory.find(i => i.reel_id === row.base_reel_id);
        const gsm = inv?.page_no ? parseFloat(inv.page_no) : extractGSMFromRmName(inv?.rm_name);
        baseReels.push({ reel_id: row.base_reel_id, rm_name: inv?.rm_name || "-", gsm, weight: parseFloat(row.base_weight) || 0 });
      }
      if (row.flute_reel_id) {
        const inv = availableInventory.find(i => i.reel_id === row.flute_reel_id);
        const gsm = inv?.page_no ? parseFloat(inv.page_no) : extractGSMFromRmName(inv?.rm_name);
        fluteReels.push({ reel_id: row.flute_reel_id, rm_name: inv?.rm_name || "-", gsm, weight: parseFloat(row.flute_weight) || 0 });
      }
    });
    setP1ReelData({ baseReels, fluteReels });
  }, [process2a.use_p1_output, process1.rows, selectedProcesses.process1, availableInventory]);

  // Calculate P1→P2A sheet hint when "Use from P1" is enabled
  useEffect(() => {
    if (!process2a.use_p1_output || !selectedProcesses.process1) {
      setP1SheetHint(null);
      return;
    }

    // 1. Total Stock Weight from P1 rows + get separate Base GSM and Flute GSM
    let totalP1Weight = 0;
    let baseGSM = 0;
    let fluteGSM = 0;
    const p1Rows = process1.rows || [];
    p1Rows.forEach(row => {
      totalP1Weight += parseFloat(row.base_weight) || 0;
      totalP1Weight += parseFloat(row.flute_weight) || 0;
      // Get Base Reel GSM
      if (row.base_reel_id && !baseGSM) {
        const invItem = availableInventory.find(i => i.reel_id === row.base_reel_id);
        baseGSM = invItem?.page_no ? parseFloat(invItem.page_no) : extractGSMFromRmName(invItem?.rm_name);
      }
      // Get Flute Reel GSM
      if (row.flute_reel_id && !fluteGSM) {
        const invItem = availableInventory.find(i => i.reel_id === row.flute_reel_id);
        fluteGSM = invItem?.page_no ? parseFloat(invItem.page_no) : extractGSMFromRmName(invItem?.rm_name);
      }
    });
    if (totalP1Weight <= 0) {
      setP1SheetHint(null);
      return;
    }

    // 2. Get reel size and cutting size
    const reelSize = parseFloat(headerData.reel_size) || 0;
    const cuttingSize = parseFloat(process2a.cutting_size || headerData.cutting_size) || 0;

    // 3. Get flute multiplier
    const selectedFlute = process1.flute_type || "";
    const fluteConfig = fluteTypes.find(f => f.name === selectedFlute);
    const fluteMultiplier = fluteConfig ? fluteConfig.multiplier : 150;

    // 4. Total GSM = Base GSM + (Flute GSM × Flute Multiplier%)
    // e.g. 180 + (120 × 150%) = 180 + 180 = 360
    const totalGSM = baseGSM + (fluteGSM * (fluteMultiplier / 100));

    // 5. Sheet Weight = (Reel Size × Cutting Size × Total GSM) / 1550
    let sheetWeightGrams = 0;
    if (reelSize > 0 && cuttingSize > 0 && totalGSM > 0) {
      sheetWeightGrams = (reelSize * cuttingSize * totalGSM) / 1550;
    }

    // 6. Total sheets = Total Stock Weight (kg → grams) / Sheet Weight (grams)
    let estimatedSheets = 0;
    if (sheetWeightGrams > 0) {
      const totalWeightGrams = totalP1Weight * 1000;
      estimatedSheets = Math.floor(totalWeightGrams / sheetWeightGrams);
    }

    setP1SheetHint({
      totalP1Weight: totalP1Weight.toFixed(1),
      baseGSM,
      fluteGSM,
      fluteType: selectedFlute || "Default",
      fluteMultiplier,
      totalGSM: totalGSM.toFixed(1),
      reelSize,
      cuttingSize,
      sheetWeightGrams: sheetWeightGrams.toFixed(2),
      estimatedSheets,
    });
  }, [process2a.use_p1_output, process2a.cutting_size, process1.rows, process1.flute_type, headerData.reel_size, headerData.cutting_size, fluteTypes, selectedProcesses.process1, availableInventory]);

  // Fetch SPD Orders (Received Purchase Orders) by company name
  const fetchSpdOrdersByCompany = async (companyName) => {
    if (!companyName) {
      setSpdOrders([]);
      return;
    }
    setLoadingSpdOrders(true);
    try {
      const response = await axios.get(`${API}/spd/orders-by-company/${encodeURIComponent(companyName)}`);
      setSpdOrders(response.data.orders || []);
    } catch (error) {
      console.error("Error fetching SPD orders:", error);
      setSpdOrders([]);
    } finally {
      setLoadingSpdOrders(false);
    }
  };

  // Handle Product ID selection and auto-populate fields
  const handleProductIdSelect = (spec) => {
    setSelectedProductId(spec.product_id);
    setProductIdOpen(false);
    setProductIdSearch("");

    // When a spec is marked "Share across companies", picking it should NOT
    // force the WO customer to the spec's owning company. We keep whatever
    // the user has already picked (or leave blank) and only fill the rest.
    const isShared = spec.shared_across_companies === true;

    // Auto-populate header data from the selected product specification
    setHeaderData(prev => ({
      ...prev,
      customer_name: isShared ? prev.customer_name : (spec.company_name || prev.customer_name),
      requested_by: isShared ? prev.requested_by : (spec.company_name || prev.requested_by),
      item_name: spec.item_name || prev.item_name,
      reel_size: spec.reel_size || prev.reel_size,
      cutting_size: spec.cutting_size || prev.cutting_size,
      length: spec.length || prev.length,
      length_mm: spec.length ? (parseFloat(spec.length) * 25.4).toFixed(2) : prev.length_mm,
      breadth: spec.breadth || prev.breadth,
      breadth_mm: spec.breadth ? (parseFloat(spec.breadth) * 25.4).toFixed(2) : prev.breadth_mm,
      height: spec.height || prev.height,
      height_mm: spec.height ? (parseFloat(spec.height) * 25.4).toFixed(2) : prev.height_mm,
      ply_type: spec.ply ? `${spec.ply} PLY` : prev.ply_type,
      item_rate: spec.rate?.toString() || prev.item_rate,  // Price per item from Prod & Material Info
      product_id: spec.product_id || prev.product_id,
    }));

    // Also populate process-specific fields
    // Process 3 - Sheet Pasting
    setProcess3(prev => ({
      ...prev,
      no_of_ply: spec.ply?.toString() || prev.no_of_ply,
      top_paper_type: spec.top_paper_type || prev.top_paper_type,
    }));

    // Process 5 & 6 - Printing related
    const hasPrinting = spec.printing === "Yes" || spec.printing_enabled === "Yes";
    const colorsStr = spec.colors?.join(", ") || spec.color || "";
    setProcess5(prev => ({
      ...prev,
      printing: hasPrinting ? "yes" : "no",
      color: colorsStr,
    }));
    setProcess6(prev => ({
      ...prev,
      printing: hasPrinting ? "Yes" : "No",
      color: colorsStr,
    }));
    setProcess8(prev => ({
      ...prev,
      printing: hasPrinting ? "yes" : "no",
      color: colorsStr,
    }));

    // Process 7 & 8 - Die IDs (support multiple)
    const specDieIds = spec.die_ids?.length > 0 ? spec.die_ids : (spec.die_id ? [spec.die_id] : []);
    setProcess7(prev => ({
      ...prev,
      die_id: specDieIds[0] || prev.die_id,
      die_ids: specDieIds,
      die_countings: specDieIds.reduce((acc, did) => ({ ...acc, [did]: prev.die_countings?.[did] || "" }), {}),
      number_of_dies: specDieIds.length > 0 ? String(specDieIds.length) : prev.number_of_dies,
    }));
    setProcess8(prev => ({
      ...prev,
      die_id: specDieIds[0] || prev.die_id,
      die_ids: specDieIds,
      die_countings: specDieIds.reduce((acc, did) => ({ ...acc, [did]: prev.die_countings?.[did] || "" }), {}),
    }));

    // Process 9 - Calico
    setProcess9(prev => ({
      ...prev,
      calico_color: spec.calico_color || prev.calico_color,
      number_of_calico: spec.number_calico || prev.number_of_calico,
    }));

    // Process 10 - Stitching
    setProcess10(prev => ({
      ...prev,
      stitching_type: spec.stitching || prev.stitching_type,
    }));

    // Update selected customer items
    // For shared-across-companies specs, do NOT replace the customer's items
    // list — preserve whichever customer is currently selected.
    if (!isShared) {
      const selectedCust = customers.find(c => c.company_name === spec.company_name);
      setSelectedCustomerItems(selectedCust?.items || []);
    }

    // Fetch available POs for the company
    if (!isShared && spec.company_name) {
      fetchSpdOrdersByCompany(spec.company_name);
    }

    toast.success(
      isShared
        ? `Loaded shared item: ${spec.product_id} - ${spec.item_name}`
        : `Loaded product: ${spec.product_id} - ${spec.item_name}`
    );

    // Auto-open the Reel Suggestion dialog so the operator can pick a base +
    // flute reel for P1 in one click. Only when P1 is selected — otherwise
    // there's nowhere to apply the result.
    if (selectedProcesses.process1) {
      setReelSuggestionSpec(spec);
      setReelSuggestionOpen(true);
    }
  };

  // Apply the picked Base + Flute reel from the suggestion dialog into P1 row 1.
  const handleReelSuggestionApply = (base, flute) => {
    if (!base || !flute) return;
    setProcess1((prev) => {
      const rows = prev.rows && prev.rows.length > 0
        ? [...prev.rows]
        : [{ base_reel_id: "", base_weight: "", flute_reel_id: "", flute_weight: "" }];
      rows[0] = {
        ...rows[0],
        base_reel_id: base.reel_id,
        base_weight: base.reel_weight?.toString?.() || rows[0].base_weight || "",
        flute_reel_id: flute.reel_id,
        flute_weight: flute.reel_weight?.toString?.() || rows[0].flute_weight || "",
      };
      return { ...prev, rows };
    });
    toast.success(`Applied: Base ${base.reel_id} | Flute ${flute.reel_id}`);
  };

  // Handle SPD Order (Purchase Order) selection with cascading auto-population
  const handleSpdOrderSelect = (spd) => {
    setSelectedSpdOrder(spd);
    
    // Extract delivery date from scheduled_datetime (format: "2026-04-11T23:38")
    const deliveryDate = spd.scheduled_datetime 
      ? spd.scheduled_datetime.split('T')[0] 
      : "";
    
    // Find matching product specification to get all dependent fields
    const matchingSpec = spd.product_id 
      ? productSpecs.find(spec => spec.product_id === spd.product_id)
      : null;
    
    // Set header data with SPD order values and product spec values
    setHeaderData(prev => ({
      ...prev,
      po_number: spd.purchase_order_number || `SPD-${spd._id.slice(-6)}`,
      spd_order_id: spd._id,
      required_quantity: spd.total_quantity?.toString() || prev.required_quantity,
      delivery_date: deliveryDate,
      item_name: spd.item_name || matchingSpec?.item_name || prev.item_name,
      // Product spec fields
      reel_size: matchingSpec?.reel_size || prev.reel_size,
      cutting_size: matchingSpec?.cutting_size || prev.cutting_size,
      length: matchingSpec?.length || prev.length,
      length_mm: matchingSpec?.length ? (parseFloat(matchingSpec.length) * 25.4).toFixed(2) : prev.length_mm,
      breadth: matchingSpec?.breadth || prev.breadth,
      breadth_mm: matchingSpec?.breadth ? (parseFloat(matchingSpec.breadth) * 25.4).toFixed(2) : prev.breadth_mm,
      height: matchingSpec?.height || prev.height,
      height_mm: matchingSpec?.height ? (parseFloat(matchingSpec.height) * 25.4).toFixed(2) : prev.height_mm,
    }));
    
    // Also update selected product ID if we found a matching spec
    if (matchingSpec) {
      setSelectedProductId(matchingSpec.product_id);
      
      // Process 3 - Sheet Pasting
      setProcess3(prev => ({
        ...prev,
        no_of_ply: matchingSpec.ply?.toString() || prev.no_of_ply,
        top_paper_type: matchingSpec.top_paper_type || prev.top_paper_type,
      }));

      // Process 5 & 6 - Printing related
      const hasPrinting = matchingSpec.printing === "Yes" || matchingSpec.printing_enabled === "Yes";
      const colorsStr = matchingSpec.colors?.join(", ") || matchingSpec.color || "";
      setProcess5(prev => ({
        ...prev,
        printing: hasPrinting ? "yes" : "no",
        color: colorsStr,
      }));
      setProcess6(prev => ({
        ...prev,
        printing: hasPrinting ? "Yes" : "No",
        color: colorsStr,
      }));
      setProcess8(prev => ({
        ...prev,
        printing: hasPrinting ? "yes" : "no",
        color: colorsStr,
      }));

      // Process 7 & 8 - Die ID
      setProcess7(prev => ({
        ...prev,
        die_id: matchingSpec.die_id || prev.die_id,
      }));
      setProcess8(prev => ({
        ...prev,
        die_id: matchingSpec.die_id || prev.die_id,
      }));

      // Process 9 - Calico
      setProcess9(prev => ({
        ...prev,
        calico_color: matchingSpec.calico_color || prev.calico_color,
        number_of_calico: matchingSpec.number_calico || prev.number_of_calico,
      }));

      // Process 10 - Stitching
      setProcess10(prev => ({
        ...prev,
        stitching_type: matchingSpec.stitching || prev.stitching_type,
      }));
      
      toast.success(`Loaded PO with product: ${matchingSpec.product_id} - ${spd.item_name}`);
    } else {
      toast.success(`PO selected: ${spd.purchase_order_number || spd._id}`);
    }
    
    setPoDropdownOpen(false);
  };

  const fetchLastWONumber = async () => {
    try {
      const response = await axios.get(`${API}/work-orders`);
      if (response.data.length > 0) {
        const maxNum = response.data.reduce((max, wo) => {
          const num = parseInt(wo.header?.work_order_number?.replace('WONO-', '') || '0');
          return num > max ? num : max;
        }, 0);
        setHeaderData(prev => ({ ...prev, work_order_number: `WONO-${String(maxNum + 1).padStart(3, '0')}` }));
      } else {
        setHeaderData(prev => ({ ...prev, work_order_number: "WONO-001" }));
      }
    } catch (error) {
      setHeaderData(prev => ({ ...prev, work_order_number: "WONO-001" }));
    }
  };

  const addProcess1Row = () => {
    setProcess1(prev => ({
      ...prev,
      rows: [...prev.rows, { base_reel_id: "", base_weight: "", flute_reel_id: "", flute_weight: "", baseOpen: false, fluteOpen: false }]
    }));
  };

  const removeProcess1Row = (index) => {
    if (process1.rows.length > 1) {
      setProcess1(prev => ({
        ...prev,
        rows: prev.rows.filter((_, i) => i !== index)
      }));
    }
  };

  const updateProcess1Row = (index, field, value) => {
    setProcess1(prev => ({
      ...prev,
      rows: prev.rows.map((row, i) => i === index ? { ...row, [field]: value } : row)
    }));
  };

  // Helper function to check if reel is used and handle selection
  const checkAndSelectReel = async (reelId, type, index = null) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/inventory/check-reel-usage/${reelId}`);
      const { is_used, work_orders, status } = response.data;
      
      if (is_used && work_orders.length > 0) {
        // Show warning dialog for any used/assigned reel
        setUsedReelWarning({
          open: true,
          reelId,
          workOrders: work_orders || [],
          type,
          index,
          callback: () => {
            // User confirmed, proceed with selection
            if (type === 'base') {
              const inventoryItem = availableInventory.find(i => i.reel_id === reelId);
              updateProcess1Row(index, 'base_reel_id', reelId);
              updateProcess1Row(index, 'baseOpen', false);
              if (inventoryItem) {
                updateProcess1Row(index, 'base_weight', inventoryItem.reel_weight?.toString() || '');
              }
            } else if (type === 'flute') {
              const inventoryItem = availableInventory.find(i => i.reel_id === reelId);
              updateProcess1Row(index, 'flute_reel_id', reelId);
              updateProcess1Row(index, 'fluteOpen', false);
              if (inventoryItem) {
                updateProcess1Row(index, 'flute_weight', inventoryItem.reel_weight?.toString() || '');
              }
            } else if (type === 'p2a') {
              // Process 2A - Sheet Cutting (Corrugated Rolls)
              const inventoryItem = availableInventory.find(i => i.reel_id === reelId);
              const newReels = [...process2a.reels];
              newReels[index] = { 
                reel_no: reelId, 
                reel_size: inventoryItem?.reel_size?.toString() || "", 
                reel_weight: inventoryItem?.qty?.toString() || "",
                reel_product: "Corrugated Roll"
              };
              setProcess2a(prev => ({ ...prev, reels: newReels }));
              setP2aReelOpen(prev => ({ ...prev, [index]: false }));
            } else if (type === 'p2b') {
              // Process 2B - Paper Cutting
              const inventoryItem = availableInventory.find(i => i.reel_id === reelId);
              const newReels = [...process2b.reels];
              newReels[index] = { 
                reel_no: reelId, 
                reel_size: inventoryItem?.reel_size?.toString() || "", 
                reel_weight: inventoryItem?.reel_weight?.toString() || "",
                reel_product: inventoryItem?.product || ""
              };
              setProcess2b(prev => ({ ...prev, reels: newReels }));
              setP2bReelOpen(prev => ({ ...prev, [index]: false }));
            } else if (type === 'p3') {
              setProcess3(prev => ({ ...prev, stock_id: reelId }));
              setP3StockOpen(false);
            }
          }
        });
        return;
      }
      
      // Not used, proceed normally
      if (type === 'base') {
        const inventoryItem = availableInventory.find(i => i.reel_id === reelId);
        updateProcess1Row(index, 'base_reel_id', reelId);
        updateProcess1Row(index, 'baseOpen', false);
        if (inventoryItem) {
          updateProcess1Row(index, 'base_weight', inventoryItem.reel_weight?.toString() || '');
        }
      } else if (type === 'flute') {
        const inventoryItem = availableInventory.find(i => i.reel_id === reelId);
        updateProcess1Row(index, 'flute_reel_id', reelId);
        updateProcess1Row(index, 'fluteOpen', false);
        if (inventoryItem) {
          updateProcess1Row(index, 'flute_weight', inventoryItem.reel_weight?.toString() || '');
        }
      } else if (type === 'p2a') {
        // Process 2A - Sheet Cutting (Corrugated Rolls)
        const inventoryItem = availableInventory.find(i => i.reel_id === reelId);
        const newReels = [...process2a.reels];
        newReels[index] = { 
          reel_no: reelId, 
          reel_size: inventoryItem?.reel_size?.toString() || "", 
          reel_weight: inventoryItem?.qty?.toString() || "",
          reel_product: "Corrugated Roll"
        };
        setProcess2a(prev => ({ ...prev, reels: newReels }));
        setP2aReelOpen(prev => ({ ...prev, [index]: false }));
      } else if (type === 'p2b') {
        // Process 2B - Paper Cutting
        const inventoryItem = availableInventory.find(i => i.reel_id === reelId);
        const newReels = [...process2b.reels];
        newReels[index] = { 
          reel_no: reelId, 
          reel_size: inventoryItem?.reel_size?.toString() || "", 
          reel_weight: inventoryItem?.reel_weight?.toString() || "",
          reel_product: inventoryItem?.product || ""
        };
        setProcess2b(prev => ({ ...prev, reels: newReels }));
        setP2bReelOpen(prev => ({ ...prev, [index]: false }));
      } else if (type === 'p3') {
        setProcess3(prev => ({ ...prev, stock_id: reelId }));
        setP3StockOpen(false);
      }
    } catch (error) {
      // If API fails, allow selection anyway
      console.error("Error checking reel usage:", error);
      if (type === 'base') {
        const inventoryItem = availableInventory.find(i => i.reel_id === reelId);
        updateProcess1Row(index, 'base_reel_id', reelId);
        updateProcess1Row(index, 'baseOpen', false);
        if (inventoryItem) {
          updateProcess1Row(index, 'base_weight', inventoryItem.reel_weight?.toString() || '');
        }
      } else if (type === 'flute') {
        const inventoryItem = availableInventory.find(i => i.reel_id === reelId);
        updateProcess1Row(index, 'flute_reel_id', reelId);
        updateProcess1Row(index, 'fluteOpen', false);
        if (inventoryItem) {
          updateProcess1Row(index, 'flute_weight', inventoryItem.reel_weight?.toString() || '');
        }
      } else if (type === 'p2a') {
        const inventoryItem = availableInventory.find(i => i.reel_id === reelId);
        const newReels = [...process2a.reels];
        newReels[index] = { 
          reel_no: reelId, 
          reel_size: inventoryItem?.reel_size?.toString() || "", 
          reel_weight: inventoryItem?.qty?.toString() || "",
          reel_product: "Corrugated Roll"
        };
        setProcess2a(prev => ({ ...prev, reels: newReels }));
        setP2aReelOpen(prev => ({ ...prev, [index]: false }));
      } else if (type === 'p2b') {
        const inventoryItem = availableInventory.find(i => i.reel_id === reelId);
        const newReels = [...process2b.reels];
        newReels[index] = { 
          reel_no: reelId, 
          reel_size: inventoryItem?.reel_size?.toString() || "", 
          reel_weight: inventoryItem?.reel_weight?.toString() || "",
          reel_product: inventoryItem?.product || ""
        };
        setProcess2b(prev => ({ ...prev, reels: newReels }));
        setP2bReelOpen(prev => ({ ...prev, [index]: false }));
      } else if (type === 'p3') {
        setProcess3(prev => ({ ...prev, stock_id: reelId }));
        setP3StockOpen(false);
      }
    }
  };

  // Helper to select inventory and auto-fill weight for P1
  const handleP1ReelSelect = (index, type, reelId) => {
    checkAndSelectReel(reelId, type, index);
  };

  // Helper to select stock_id for P3 with used reel warning
  const handleP3StockSelect = (reelId) => {
    checkAndSelectReel(reelId, 'p3', null);
  };

  // Filter inventory to exclude used stocks for P1 and P2
  // Only show items that are 'available' or 'return' status (exclude 'stock_used', 'sent_for_printing', 'sent_for_corrugation', etc.)
  const availableStockInventory = availableInventory.filter(inv => 
    inv.status === 'available' || inv.status === 'return' || !inv.status
  );

  // Filtered inventory for P2A search (from available stock only)
  const filteredP2aInventory = availableStockInventory.filter(inv => {
    if (!p2aSearchTerm) return true;
    const term = p2aSearchTerm.toLowerCase();
    return (
      inv.reel_id?.toLowerCase().includes(term) ||
      inv.rm_name?.toLowerCase().includes(term) ||
      inv.reel_size?.toString().includes(term)
    );
  });

  // Filtered inventory for P2B search (from available stock only)
  const filteredP2bInventory = availableStockInventory.filter(inv => {
    if (!p2bSearchTerm) return true;
    const term = p2bSearchTerm.toLowerCase();
    return (
      inv.reel_id?.toLowerCase().includes(term) ||
      inv.rm_name?.toLowerCase().includes(term) ||
      inv.reel_size?.toString().includes(term)
    );
  });

  const handleSave = async () => {
    try {
      // Only include selected processes in the save
      const processesToSave = {};
      if (selectedProcesses.process1) {
        processesToSave.process1 = process1;
      }
      if (selectedProcesses.process2a) {
        const p2aData = { ...process2a };
        // Include reel split info if applicable (split happens at P2A with 4Bar Rotary Creasing)
        if (reelSplitInfo.has_split) {
          p2aData.reel_split = {
            has_split: true,
            stock_reel_size: reelSplitInfo.stock_reel_size,
            item_reel_size: reelSplitInfo.item_reel_size,
            remaining_size: reelSplitInfo.remaining_size,
            handling: reelSplitInfo.handling,
            assigned_wo: reelSplitInfo.assigned_wo,
            notes: reelSplitInfo.notes
          };
        }
        processesToSave.process2a = p2aData;
      }
      if (selectedProcesses.process2b) processesToSave.process2b = process2b;  // Paper Cutting
      if (selectedProcesses.process3) processesToSave.process3 = process3;
      if (selectedProcesses.process4) processesToSave.process4 = process4;
      if (selectedProcesses.process5) {
        const p5Data = { ...process5 };
        // Include reel split info if split detected at P5 level
        if (reelSplitInfo.has_split && reelSplitInfo.split_source === "P5") {
          p5Data.reel_split = {
            has_split: true,
            stock_reel_size: reelSplitInfo.stock_reel_size,
            item_reel_size: reelSplitInfo.item_reel_size,
            remaining_size: reelSplitInfo.remaining_size,
            handling: reelSplitInfo.handling,
            assigned_wo: reelSplitInfo.assigned_wo,
            notes: reelSplitInfo.notes
          };
        }
        processesToSave.process5 = p5Data;
      }
      if (selectedProcesses.process6) processesToSave.process6 = process6;
      if (selectedProcesses.process6a) processesToSave.process6a = process6a;
      if (selectedProcesses.process7) processesToSave.process7 = process7;
      if (selectedProcesses.process8) processesToSave.process8 = process8;
      if (selectedProcesses.process9) processesToSave.process9 = process9;
      if (selectedProcesses.process9a) processesToSave.process9a = process9a;
      if (selectedProcesses.process10) processesToSave.process10 = { ...process10, total_boxes: totalBoxesCalculated };
      // Mandatory processes - always included
      processesToSave.process11 = process11;
      processesToSave.process12 = process12;
      
      const workOrderData = {
        header: headerData,
        processes: processesToSave,
        selected_processes: selectedProcesses,
      };

      if (isEditMode) {
        await axios.put(`${API}/work-orders/${editWorkOrderId}`, workOrderData);
        toast.success("Work Order updated successfully!");
      } else {
        await axios.post(`${API}/work-orders`, workOrderData);
        // Check if we need to split the SPD order (if quantity used is less than PO quantity)
        if (selectedSpdOrder && headerData.spd_order_id) {
          const usedQty = parseInt(headerData.required_quantity) || 0;
          const totalQty = selectedSpdOrder.total_quantity || 0;
          
          if (usedQty > 0 && usedQty < totalQty) {
            try {
              await axios.post(`${API}/spd/orders/split?parent_id=${headerData.spd_order_id}&used_quantity=${usedQty}`);
              toast.info(`Balance quantity (${totalQty - usedQty}) created as new PO`);
            } catch (splitError) {
              console.error("Error splitting SPD order:", splitError);
            }
          }
        }
        toast.success("Work Order created successfully!");
      }
      navigate("/workorder#saved");
    } catch (error) {
      console.error("Error saving work order:", error);
      toast.error(isEditMode ? "Failed to update work order" : "Failed to create work order");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleProcess = (processKey) => {
    setExpandedProcesses(prev => ({ ...prev, [processKey]: !prev[processKey] }));
  };

  return (
    <>
      <div className="min-h-screen p-6 no-print">
        <div className="max-w-7xl mx-auto">
          {/* Modern Header - Subtle Cyan Theme */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl shadow-2xl p-6 mb-6 border border-[#374151]">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <Button onClick={() => navigate("/workorder")} variant="ghost" className="text-gray-400 hover:text-white" data-testid="back-to-workorder">
                  <ArrowLeft className="h-5 w-5 mr-2" /> Back
                </Button>
                <div>
                  <h1 className="text-4xl font-black text-[#9CA3AF]">{isEditMode ? 'EDIT WORK ORDER' : 'WORK ORDER CREATION'}</h1>
                  <p className="text-lg text-[#9CA3AF] mt-1">Challenge Packaging Industries</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button onClick={handlePrint} variant="outline" className="bg-slate-800 text-[#F9FAFB] border-[#374151] hover:bg-[#1F2937] transition-all">
                  <Printer className="h-5 w-5 mr-2" />
                  Print
                </Button>
                <Button onClick={handleSave} className="bg-cyan-600 text-white hover:bg-cyan-700 transition-all font-bold">
                  <Save className="h-5 w-5 mr-2" />
                  {isEditMode ? 'Update Work Order' : 'Save Work Order'}
                </Button>
              </div>
            </div>
          </div>

          {/* Process Selection - Professional Design */}
          <Card className="mb-6 bg-[#111827] border border-[#374151] shadow-sm">
            <CardHeader className="bg-[#1F2937] border-b border-[#374151]">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-2xl font-bold text-[#F9FAFB] tracking-tight">Manufacturing Processes</CardTitle>
                  <p className="text-sm text-[#9CA3AF]">Click to toggle processes · Green = Selected · Gray = Unselected</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    type="button"
                    onClick={() => {
                      setSelectedProcesses({
                        process1: true, process2a: true, process2b: true, process3: true, process4: true, process5: true,
                        process6: true, process6a: true, process7: true, process8: true, process9: true, process9a: true, process10: true,
                        process11: true, process12: true
                      });
                      setProcessOrder(['process1','process2a','process2b','process3','process4','process5','process6','process6a','process7','process8','process9','process9a','process10','process11','process12']);
                    }}
                    className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium"
                    size="sm"
                    data-testid="select-all-processes"
                  >
                    Select All
                  </Button>
                  <Button 
                    type="button"
                    onClick={() => {
                      setSelectedProcesses({
                        process1: false, process2a: false, process2b: false, process3: false, process4: false, process5: false,
                        process6: false, process6a: false, process7: false, process8: false, process9: false, process9a: false, process10: false,
                        process11: true, process12: true
                      });
                      setProcessOrder(['process11','process12']);
                    }}
                    className="bg-[#374151] hover:bg-[#4B5563] text-[#F9FAFB] font-medium"
                    size="sm"
                    data-testid="clear-all-processes"
                  >
                    Clear All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 bg-[#111827]">
              {/* Quick Toggle Strip - Professional Unified Design */}
              <div className="flex flex-wrap gap-2 justify-center">
                {[
                  { key: 'process1', label: 'P1', fullLabel: 'Corrugation', mandatory: false },
                  { key: 'process2a', label: 'P2A', fullLabel: 'Sheet Cutting', mandatory: false },
                  { key: 'process2b', label: 'P2B', fullLabel: 'Paper Cutting', mandatory: false },
                  { key: 'process3', label: 'P3', fullLabel: 'Sheet Pasting', mandatory: false },
                  { key: 'process4', label: 'P4', fullLabel: 'Slitter Scorer', mandatory: false },
                  { key: 'process5', label: 'P5', fullLabel: 'Rotary Creasing', mandatory: false },
                  { key: 'process6', label: 'P6', fullLabel: 'RS4 Print', mandatory: false },
                  { key: 'process6a', label: 'P6A', fullLabel: 'Eccentric Slotter', mandatory: false },
                  { key: 'process7', label: 'P7', fullLabel: 'Flat Punching', mandatory: false },
                  { key: 'process8', label: 'P8', fullLabel: 'Rotary Die', mandatory: false },
                  { key: 'process9', label: 'P9', fullLabel: 'Calico Pasting', mandatory: false },
                  { key: 'process9a', label: 'P9A', fullLabel: 'Board Drying', mandatory: false },
                  { key: 'process10', label: 'P10', fullLabel: 'Stitching', mandatory: false },
                  { key: 'process11', label: 'P11', fullLabel: 'Loading', mandatory: true },
                  { key: 'process12', label: 'P12', fullLabel: 'Transport', mandatory: true },
                ].map(({ key, label, fullLabel, mandatory }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      if (mandatory) return;
                      const isCurrentlySelected = selectedProcesses[key];
                      setSelectedProcesses(prev => ({ ...prev, [key]: !prev[key] }));
                      // Track selection order: add at canonical position when selected, remove when deselected.
                      if (!isCurrentlySelected) {
                        // Canonical sequence — used to place a re-enabled process in its
                        // natural slot instead of dumping it at the end (e.g. P6A
                        // belongs between P6 and P7, not after P10).
                        const CANONICAL = [
                          'process1','process2a','process2b','process3','process4','process5',
                          'process6','process6a','process7','process8','process9','process9a',
                          'process10','process11','process12'
                        ];
                        const canonicalIdx = CANONICAL.indexOf(key);
                        setProcessOrder(prev => {
                          const filtered = prev.filter(k => k !== key);
                          if (canonicalIdx < 0) return [...filtered, key];
                          // Insert before the first already-present successor in CANONICAL order.
                          const successors = CANONICAL.slice(canonicalIdx + 1);
                          const insertBeforeIdx = filtered.findIndex(k => successors.includes(k));
                          if (insertBeforeIdx >= 0) {
                            return [...filtered.slice(0, insertBeforeIdx), key, ...filtered.slice(insertBeforeIdx)];
                          }
                          return [...filtered, key];
                        });
                      } else {
                        setProcessOrder(prev => prev.filter(k => k !== key));
                      }
                    }}
                    disabled={mandatory}
                    data-testid={`toggle-${key}`}
                    className={`
                      relative group px-4 py-3 rounded-lg font-semibold text-sm transition-all duration-200 min-w-[90px]
                      ${mandatory 
                        ? 'bg-blue-600 text-white border-2 border-blue-600 cursor-not-allowed shadow-md ring-2 ring-blue-400/30' 
                        : selectedProcesses[key]
                          ? 'bg-blue-600 text-white border-2 border-blue-600 shadow-lg ring-2 ring-blue-400/30'
                          : 'bg-[#1F2937] text-[#9CA3AF] hover:bg-[#374151] hover:text-white border-2 border-[#374151]'
                      }
                    `}
                    title={`${label} - ${fullLabel}${mandatory ? ' (Mandatory)' : ''}`}
                  >
                    <span className="block text-lg">{label}</span>
                    <span className="block text-[10px] font-normal opacity-80 truncate">{fullLabel}</span>
                  </button>
                ))}
              </div>
              <p className="text-center text-xs text-[#6B7280] mt-3">
                {Object.values(selectedProcesses).filter(Boolean).length} of {Object.keys(selectedProcesses).length} processes selected
              </p>
            </CardContent>
          </Card>

          {/* Header Information - Professional Layout */}
          <Card className="mb-6 bg-[#111827] border border-[#374151] shadow-sm">
            <CardHeader className="bg-[#1F2937] border-b border-[#374151]">
              <CardTitle className="text-2xl font-bold text-[#F9FAFB] tracking-tight">Work Order Details</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Product ID Selection - NEW */}
              <div className="bg-gradient-to-r from-cyan-900/30 to-slate-800/50 rounded-lg p-4 border border-[#374151]">
                <Label className="text-[#9CA3AF] font-bold text-lg mb-2 block">Select Product from Prod & Material Info</Label>
                <p className="text-gray-400 text-sm mb-3">Select a Product ID to auto-populate all fields from the master database</p>
                <Popover open={productIdOpen} onOpenChange={setProductIdOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={productIdOpen}
                      className="w-full md:w-96 justify-between bg-[#111827] border-cyan-500/50 text-white hover:border-[#3B82F6] transition-all h-12 text-left"
                      data-testid="product-id-dropdown"
                    >
                      {selectedProductId || "Search & Select Product ID..."}
                      <Search className="ml-2 h-5 w-5 shrink-0 text-[#9CA3AF]" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[500px] p-0 bg-[#111827] border-slate-500/50" align="start">
                    <Command className="bg-transparent">
                      <CommandInput
                        placeholder="Search by Product ID, Company, Item Name..."
                        value={productIdSearch}
                        onValueChange={setProductIdSearch}
                        className="text-white"
                      />
                      <CommandList>
                        <CommandEmpty>
                          <p className="text-gray-400 text-sm p-4 text-center">
                            No matching products found. Add products in Prod & Material Info page.
                          </p>
                        </CommandEmpty>
                        <CommandGroup heading="Products from Prod & Material Info" className="max-h-80 overflow-auto">
                          {productSpecs
                            .filter(spec => {
                              if (!productIdSearch) return true;
                              const search = productIdSearch.toLowerCase();
                              return (
                                spec.product_id?.toLowerCase().includes(search) ||
                                spec.company_name?.toLowerCase().includes(search) ||
                                spec.item_name?.toLowerCase().includes(search)
                              );
                            })
                            .map((spec) => (
                              <CommandItem
                                key={spec.id}
                                value={`${spec.product_id} ${spec.company_name} ${spec.item_name}`}
                                onSelect={() => handleProductIdSelect(spec)}
                                className="text-white hover:bg-cyan-500/20 cursor-pointer py-3"
                              >
                                <Check className={cn("mr-2 h-4 w-4", selectedProductId === spec.product_id ? "opacity-100 text-[#9CA3AF]" : "opacity-0")} />
                                <div className="flex flex-col flex-1">
                                  <span className="font-semibold text-[#9CA3AF]">
                                    {spec.product_id}
                                    {spec.shared_across_companies && (
                                      <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                                        Shared
                                      </span>
                                    )}
                                  </span>
                                  <span className="text-sm text-gray-300">{spec.company_name} - {spec.item_name}</span>
                                  <span className="text-xs text-gray-500">
                                    {spec.length}×{spec.breadth}×{spec.height}{spec.dimension_unit ? ` ${spec.dimension_unit}` : ""} | {spec.ply} Ply | {spec.stitching}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Customer & Key Info */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Company Name *</Label>
                  <Select 
                    value={headerData.customer_name}
                    onValueChange={(val) => {
                      const selectedCust = customers.find(c => c.company_name === val);
                      setHeaderData(prev => ({ ...prev, customer_name: val, requested_by: val, item_name: "", po_number: "", spd_order_id: "" }));
                      setSelectedCustomerItems(selectedCust?.items || []);
                      setSelectedSpdOrder(null);
                      // Fetch SPD orders (Received POs) for this company
                      fetchSpdOrdersByCompany(val);
                    }}
                  >
                    <SelectTrigger className="bg-[#111827] border-[#374151] text-white hover:border-[#3B82F6] transition-all mt-1">
                      <SelectValue placeholder="Select Company..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111827] border-slate-500/50 max-h-60">
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.company_name} className="text-white">
                          {c.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Work Order Number *</Label>
                  <Input value={headerData.work_order_number} className="bg-gradient-to-r from-green-900 to-green-800 border-green-500 text-green-200 font-bold text-lg mt-1" readOnly />
                </div>
                <div>
                  <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Delivery Date *</Label>
                  <div className="relative mt-1">
                    <Input type="date" value={headerData.delivery_date} onChange={(e) => setHeaderData(prev => ({ ...prev, delivery_date: e.target.value }))} className="bg-[#111827] border-[#374151] text-white hover:border-[#3B82F6] transition-all pr-10 [color-scheme:dark]" />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF] pointer-events-none" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Required Quantity *</Label>
                  <Input type="number" value={headerData.required_quantity} onChange={(e) => setHeaderData(prev => ({ ...prev, required_quantity: e.target.value }))} className="bg-[#111827] border-[#374151] text-white hover:border-[#3B82F6] transition-all mt-1" />
                  {selectedSpdOrder && parseInt(headerData.required_quantity) !== selectedSpdOrder.total_quantity && (
                    <p className="text-xs text-yellow-400 mt-1">
                      PO Qty: {selectedSpdOrder.total_quantity} | Balance will create new PO
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Item Name *</Label>
                  <Popover open={itemNameOpen} onOpenChange={setItemNameOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={itemNameOpen}
                        className="w-full justify-between bg-[#111827] border-[#374151] text-white hover:border-[#3B82F6] transition-all mt-1 h-10"
                      >
                        {headerData.item_name || (headerData.customer_name ? "Select Item..." : "Select Company First")}
                        <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[350px] p-0 bg-[#111827] border-slate-500/50" align="start">
                      <Command className="bg-transparent">
                        <CommandInput
                          placeholder="Search item..."
                          value={itemNameSearch}
                          onValueChange={setItemNameSearch}
                          className="text-white"
                        />
                        <CommandList>
                          <CommandEmpty>
                            <div className="p-2 text-center">
                              <p className="text-gray-400 text-sm mb-2">
                                {selectedCustomerItems.length === 0 
                                  ? "No items for this customer. Add items in WO Database." 
                                  : "No matching items"}
                              </p>
                              {itemNameSearch && (
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setHeaderData(prev => ({ ...prev, item_name: itemNameSearch }));
                                    setItemNameOpen(false);
                                    setItemNameSearch("");
                                  }}
                                  className="bg-slate-500 hover:bg-slate-600 text-black"
                                >
                                  Use "{itemNameSearch}"
                                </Button>
                              )}
                            </div>
                          </CommandEmpty>
                          <CommandGroup heading={`Items for ${headerData.customer_name}`}>
                            {selectedCustomerItems.filter(item => 
                              !itemNameSearch || item.item_name?.toLowerCase().includes(itemNameSearch.toLowerCase())
                            ).map((item, idx) => (
                              <CommandItem
                                key={item.item_name || `item-${idx}`}
                                value={item.item_name}
                                onSelect={() => {
                                  setHeaderData(prev => ({ ...prev, item_name: item.item_name }));
                                  setItemNameOpen(false);
                                  setItemNameSearch("");
                                }}
                                className="text-white hover:bg-slate-500/20 cursor-pointer"
                              >
                                <Check className={cn("mr-2 h-4 w-4", headerData.item_name === item.item_name ? "opacity-100" : "opacity-0")} />
                                <span>{item.item_name}</span>
                                {item.price > 0 && (
                                  <span className="ml-auto text-[#9CA3AF] text-sm">₹{item.price?.toFixed(2)}</span>
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Reel Size</Label>
                  <Input value={headerData.reel_size} onChange={(e) => setHeaderData(prev => ({ ...prev, reel_size: e.target.value }))} className="bg-[#111827] border-[#374151] text-white hover:border-[#3B82F6] transition-all mt-1" />
                </div>
                <div>
                  <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Cutting Size</Label>
                  <Input value={headerData.cutting_size} onChange={(e) => setHeaderData(prev => ({ ...prev, cutting_size: e.target.value }))} className="bg-[#111827] border-[#374151] text-white hover:border-[#3B82F6] transition-all mt-1" />
                </div>
                <div>
                  <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Purchase Order</Label>
                  <Popover open={poDropdownOpen} onOpenChange={setPoDropdownOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between bg-[#111827] border-[#374151] text-white hover:border-[#3B82F6] transition-all mt-1 h-10"
                        disabled={!headerData.customer_name}
                      >
                        {headerData.po_number || (headerData.customer_name ? "Select PO..." : "Select company first")}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[350px] p-0 bg-[#111827] border-slate-500/50">
                      <Command className="bg-transparent">
                        <CommandInput placeholder="Search PO..." className="text-white" />
                        <CommandList>
                          <CommandEmpty className="text-gray-400 py-4 text-center text-sm">
                            {loadingSpdOrders ? "Loading..." : spdOrders.length === 0 ? "No POs available for this company" : "No matching PO"}
                          </CommandEmpty>
                          <CommandGroup heading="Available Purchase Orders">
                            {spdOrders.map((spd) => (
                              <CommandItem
                                key={spd._id}
                                value={spd.purchase_order_number || spd._id}
                                onSelect={() => handleSpdOrderSelect(spd)}
                                className="text-white hover:bg-slate-500/20 cursor-pointer"
                              >
                                <Check className={cn("mr-2 h-4 w-4", headerData.spd_order_id === spd._id ? "opacity-100" : "opacity-0")} />
                                <div className="flex flex-col">
                                  <span className="font-medium">{spd.purchase_order_number || `SPD-${spd._id.slice(-6)}`}</span>
                                  <span className="text-xs text-gray-400">
                                    {spd.item_name} • Qty: {spd.total_quantity}
                                    {spd.scheduled_datetime && ` • Due: ${new Date(spd.scheduled_datetime).toLocaleDateString()}`}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Order Received Date</Label>
                  <div className="relative mt-1">
                    <Input type="date" value={headerData.order_received_date} onChange={(e) => setHeaderData(prev => ({ ...prev, order_received_date: e.target.value }))} className="bg-[#111827] border-[#374151] text-white hover:border-[#3B82F6] transition-all pr-10 [color-scheme:dark]" />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF] pointer-events-none" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">WO Creation Date</Label>
                  <div className="relative mt-1">
                    <Input type="date" value={headerData.work_order_creation_date} className="bg-[#111827] border-[#374151] text-gray-400 pr-10 [color-scheme:dark]" readOnly />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Box Dimensions - Enhanced Design */}
              <div className="border-t-2 border-[#374151] pt-6">
                <h4 className="text-xl font-bold text-[#9CA3AF] mb-4 flex items-center">
                  <span className="bg-slate-500 text-black px-3 py-1 rounded-lg mr-3">📦</span>
                  Box Dimensions
                </h4>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Unit</Label>
                    <Select value={headerData.dimension_unit} onValueChange={(val) => setHeaderData(prev => ({ ...prev, dimension_unit: val }))}>
                      <SelectTrigger className="bg-[#111827] border-[#374151] text-white hover:border-[#3B82F6] transition-all mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mm">Millimeters (mm)</SelectItem>
                        <SelectItem value="inches">Inches</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Length ({headerData.dimension_unit})</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={headerData.dimension_unit === 'mm' ? headerData.length_mm : headerData.length}
                      onChange={(e) => setHeaderData(prev => ({
                        ...prev,
                        [headerData.dimension_unit === 'mm' ? 'length_mm' : 'length']: e.target.value
                      }))}
                      className="bg-[#111827] border-[#374151] text-white hover:border-[#3B82F6] transition-all mt-1"
                    />
                    <p className="text-xs text-[#F9FAFB]/60 mt-1">
                      = {headerData.dimension_unit === 'mm' ? `${headerData.length} in` : `${headerData.length_mm} mm`}
                    </p>
                  </div>

                  <div>
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Breadth ({headerData.dimension_unit})</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={headerData.dimension_unit === 'mm' ? headerData.breadth_mm : headerData.breadth}
                      onChange={(e) => setHeaderData(prev => ({
                        ...prev,
                        [headerData.dimension_unit === 'mm' ? 'breadth_mm' : 'breadth']: e.target.value
                      }))}
                      className="bg-[#111827] border-[#374151] text-white hover:border-[#3B82F6] transition-all mt-1"
                    />
                    <p className="text-xs text-[#F9FAFB]/60 mt-1">
                      = {headerData.dimension_unit === 'mm' ? `${headerData.breadth} in` : `${headerData.breadth_mm} mm`}
                    </p>
                  </div>

                  <div>
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Height ({headerData.dimension_unit})</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={headerData.dimension_unit === 'mm' ? headerData.height_mm : headerData.height}
                      onChange={(e) => setHeaderData(prev => ({
                        ...prev,
                        [headerData.dimension_unit === 'mm' ? 'height_mm' : 'height']: e.target.value
                      }))}
                      className="bg-[#111827] border-[#374151] text-white hover:border-[#3B82F6] transition-all mt-1"
                    />
                    <p className="text-xs text-[#F9FAFB]/60 mt-1">
                      = {headerData.dimension_unit === 'mm' ? `${headerData.height} in` : `${headerData.height_mm} mm`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Work Order Details - UPs, Joint Type, Ply Type */}
              <div className="border-t-2 border-[#374151] pt-6 mt-6">
                <h4 className="text-xl font-bold text-[#9CA3AF] mb-4 flex items-center">
                  <span className="bg-purple-600 text-white px-3 py-1 rounded-lg mr-3">⚙️</span>
                  Work Order Details
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  {/* UPs Dropdown with Add More option */}
                  <div>
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">UPs</Label>
                    <div className="flex gap-2 mt-1">
                      <Select value={headerData.ups} onValueChange={(val) => setHeaderData(prev => ({ ...prev, ups: val }))}>
                        <SelectTrigger className="bg-[#111827] border-[#374151] text-white hover:border-[#3B82F6] transition-all flex-1" data-testid="ups-dropdown">
                          <SelectValue placeholder="Select UPs..." />
                        </SelectTrigger>
                        <SelectContent>
                          {upsList.map((up) => (
                            <SelectItem key={up} value={up}>{up}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Popover open={showAddUp} onOpenChange={setShowAddUp}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="icon" className="bg-[#111827] border-[#374151] text-[#9CA3AF] hover:bg-purple-600/20 hover:border-purple-500" data-testid="add-up-btn">
                            <Plus className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-48 p-3 bg-[#111827] border-[#374151]">
                          <div className="space-y-2">
                            <Label className="text-[#9CA3AF] text-xs">Add Custom UP</Label>
                            <Input 
                              placeholder="e.g., 16 UP" 
                              value={newUpValue}
                              onChange={(e) => setNewUpValue(e.target.value)}
                              className="bg-[#0B0F19] border-[#374151] text-white text-sm"
                            />
                            <Button 
                              size="sm" 
                              className="w-full bg-purple-600 hover:bg-purple-700"
                              onClick={() => {
                                if (newUpValue.trim()) {
                                  const upVal = newUpValue.trim().toUpperCase().includes('UP') ? newUpValue.trim() : `${newUpValue.trim()} UP`;
                                  if (!upsList.includes(upVal)) {
                                    setUpsList(prev => [...prev, upVal]);
                                  }
                                  setHeaderData(prev => ({ ...prev, ups: upVal }));
                                  setNewUpValue("");
                                  setShowAddUp(false);
                                }
                              }}
                            >
                              Add
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Joint Type Dropdown */}
                  <div>
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Joint Type</Label>
                    <Select value={headerData.joint_type} onValueChange={(val) => setHeaderData(prev => ({ ...prev, joint_type: val }))}>
                      <SelectTrigger className="bg-[#111827] border-[#374151] text-white hover:border-[#3B82F6] transition-all mt-1" data-testid="joint-type-dropdown">
                        <SelectValue placeholder="Select Joint Type..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Single">Single Joint</SelectItem>
                        <SelectItem value="Double">Double Joint</SelectItem>
                        <SelectItem value="Four">Four Joint</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Ply Type (Auto-populated from Product Selection) */}
                  <div>
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Ply Type</Label>
                    <div className="mt-1 px-4 py-2.5 bg-gradient-to-r from-purple-900/30 to-purple-800/20 border border-purple-500/50 rounded-md">
                      <span className="text-purple-300 font-semibold text-lg" data-testid="ply-type-display">
                        {headerData.ply_type || process3.no_of_ply ? `${process3.no_of_ply} PLY` : 'Select Product'}
                      </span>
                      <p className="text-xs text-purple-400/70 mt-0.5">Auto-populated from Product</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Process cards - rendered in selection order */}
          <div className="flex flex-col">

          {/* PROCESS 1 - CORRUGATION - Professional Design */}
          {selectedProcesses.process1 && (
            <Card style={{ order: processOrder.indexOf('process1') >= 0 ? processOrder.indexOf('process1') : 99 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm">
              <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all" onClick={() => toggleProcess('process1')}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="bg-[#2563EB] text-white px-3 py-1 rounded-md font-bold text-sm">P1</span>
                    <CardTitle className="text-lg text-[#F9FAFB] font-semibold">CORRUGATION MACHINE</CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-[#111827] border border-[#374151] rounded-md px-3 py-1.5 hover:border-[#3B82F6] transition-all">
                      <Calendar className="h-4 w-4 text-[#9CA3AF]" />
                      <Input type="date" value={process1.date} onChange={(e) => { e.stopPropagation(); setProcess1(prev => ({ ...prev, date: e.target.value })); }} className="w-36 bg-transparent border-0 text-white p-0 h-auto focus-visible:ring-0 [color-scheme:dark]" placeholder="Select date" data-testid="process1-date" />
                    </div>
                    {expandedProcesses.process1 ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                  </div>
                </div>
              </CardHeader>
              {expandedProcesses.process1 && (
                <CardContent className="p-4 bg-[#111827]">
                  {/* Reel-Suggestion launcher — only when a Product is selected */}
                  {selectedProductId && (
                    <div className="mb-3 flex items-center justify-between gap-3 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-md">
                      <span className="text-emerald-300 text-xs">
                        Need help picking reels for <strong>{selectedProductId}</strong>?
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs bg-emerald-600/20 border-emerald-500/50 text-emerald-200 hover:bg-emerald-600/40"
                        onClick={(e) => {
                          e.stopPropagation();
                          const spec = productSpecs.find((s) => s.product_id === selectedProductId);
                          if (spec) {
                            setReelSuggestionSpec(spec);
                            setReelSuggestionOpen(true);
                          }
                        }}
                        data-testid="open-reel-suggestion-btn"
                      >
                        Suggest Reels
                      </Button>
                    </div>
                  )}
                  <div className="mb-4">
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Type of Flute</Label>
                    <Select value={process1.flute_type} onValueChange={(val) => setProcess1(prev => ({ ...prev, flute_type: val }))}>
                      <SelectTrigger className="bg-[#111827] border-[#374151] text-white mt-1 w-64" data-testid="process1-flute-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Narrow Flute">Narrow Flute</SelectItem>
                        <SelectItem value="E Flute">E Flute</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-2 border-[#374151]">
                      <thead>
                        <tr className="bg-slate-500/10">
                          <th className="border border-[#374151] p-2 text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Base Reel ID</th>
                          <th className="border border-[#374151] p-2 text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Weight (kg)</th>
                          <th className="border border-[#374151] p-2 text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Flute Reel ID</th>
                          <th className="border border-[#374151] p-2 text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Weight (kg)</th>
                          <th className="border border-[#374151] p-2 text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {process1.rows.map((row, i) => (
                          <tr key={`p1-row-${row.base_reel_id || ''}-${row.flute_reel_id || ''}-${i}`} className="hover:bg-slate-500/5">
                            <td className="border border-[#374151] p-1">
                              <Popover open={row.baseOpen} onOpenChange={(open) => updateProcess1Row(i, 'baseOpen', open)}>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    className="w-full justify-between bg-[#111827] border-[#374151] text-white h-9 hover:bg-[#1F2937]"
                                  >
                                    {row.base_reel_id || "Search Base Reel..."}
                                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[300px] p-0 bg-[#111827] border-[#374151]">
                                  <Command className="bg-[#111827]">
                                    <CommandInput placeholder="Search by ID, RM Name..." className="bg-[#111827] text-white" />
                                    <CommandList>
                                      <CommandEmpty>No inventory found.</CommandEmpty>
                                      <CommandGroup className="max-h-60 overflow-auto">
                                        {availableStockInventory.map((inv) => (
                                          <CommandItem
                                            key={inv.id}
                                            value={`${inv.reel_id} ${inv.rm_name} ${inv.reel_size || ""}`}
                                            onSelect={() => handleP1ReelSelect(i, 'base', inv.reel_id)}
                                            className="text-white hover:bg-slate-500/20 cursor-pointer"
                                          >
                                            <Check
                                              className={cn(
                                                "mr-2 h-4 w-4",
                                                row.base_reel_id === inv.reel_id ? "opacity-100" : "opacity-0"
                                              )}
                                            />
                                            <span className="flex-1">
                                              {inv.reel_id} - {inv.rm_name}{inv.reel_size ? ` (Size: ${inv.reel_size}")` : ""} ({inv.reel_weight}kg)
                                            </span>
                                            {inv.status === 'stock_used' && (
                                              <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-red-500/20 text-[#9CA3AF] rounded">Used</span>
                                            )}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </td>
                            <td className="border border-[#374151] p-1">
                              <Input type="number" value={row.base_weight} onChange={(e) => updateProcess1Row(i, 'base_weight', e.target.value)} className="bg-[#111827] border-[#374151] text-white h-9" />
                            </td>
                            <td className="border border-[#374151] p-1">
                              <Popover open={row.fluteOpen} onOpenChange={(open) => updateProcess1Row(i, 'fluteOpen', open)}>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    className="w-full justify-between bg-[#111827] border-[#374151] text-white h-9 hover:bg-[#1F2937]"
                                  >
                                    {row.flute_reel_id || "Search Flute Reel..."}
                                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[300px] p-0 bg-[#111827] border-[#374151]">
                                  <Command className="bg-[#111827]">
                                    <CommandInput placeholder="Search by ID, RM Name..." className="bg-[#111827] text-white" />
                                    <CommandList>
                                      <CommandEmpty>No inventory found.</CommandEmpty>
                                      <CommandGroup className="max-h-60 overflow-auto">
                                        {availableStockInventory.map((inv) => (
                                          <CommandItem
                                            key={inv.id}
                                            value={`${inv.reel_id} ${inv.rm_name} ${inv.reel_size || ""}`}
                                            onSelect={() => handleP1ReelSelect(i, 'flute', inv.reel_id)}
                                            className="text-white hover:bg-slate-500/20 cursor-pointer"
                                          >
                                            <Check
                                              className={cn(
                                                "mr-2 h-4 w-4",
                                                row.flute_reel_id === inv.reel_id ? "opacity-100" : "opacity-0"
                                              )}
                                            />
                                            <span className="flex-1">
                                              {inv.reel_id} - {inv.rm_name}{inv.reel_size ? ` (Size: ${inv.reel_size}")` : ""} ({inv.reel_weight}kg)
                                            </span>
                                            {inv.status === 'stock_used' && (
                                              <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-red-500/20 text-[#9CA3AF] rounded">Used</span>
                                            )}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </td>
                            <td className="border border-[#374151] p-1">
                              <Input type="number" value={row.flute_weight} onChange={(e) => updateProcess1Row(i, 'flute_weight', e.target.value)} className="bg-[#111827] border-[#374151] text-white h-9" />
                            </td>
                            <td className="border border-[#374151] p-1 text-center">
                              {i === process1.rows.length - 1 ? (
                                <Button size="sm" onClick={addProcess1Row} className="bg-slate-500 hover:bg-slate-600 text-black h-9">
                                  <Plus className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button size="sm" variant="destructive" onClick={() => removeProcess1Row(i)} className="h-9">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* PROCESS 2A - SHEET CUTTING - Professional Design */}
          {selectedProcesses.process2a && (
            <Card style={{ order: processOrder.indexOf('process2a') >= 0 ? processOrder.indexOf('process2a') : 99 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm">
              <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all py-3" onClick={() => toggleProcess('process2a')}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="bg-[#2563EB] text-white px-3 py-1 rounded-md font-bold text-sm">P2A</span>
                    <CardTitle className="text-lg text-[#F9FAFB] font-semibold">SHEET CUTTING MACHINE</CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-[#111827] border border-[#374151] rounded-md px-3 py-1.5 hover:border-[#3B82F6] transition-all">
                      <Calendar className="h-4 w-4 text-[#9CA3AF]" />
                      <Input type="date" value={process2a.date} onChange={(e) => { e.stopPropagation(); setProcess2a(prev => ({ ...prev, date: e.target.value })); }} className="w-36 bg-transparent border-0 text-white p-0 h-auto focus-visible:ring-0 [color-scheme:dark]" placeholder="Select date" data-testid="process2a-date" />
                    </div>
                    {expandedProcesses.process2a ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                  </div>
                </div>
              </CardHeader>
              {expandedProcesses.process2a && (
                <CardContent className="p-4 bg-[#111827]">

                  {/* Corrugated Rolls Section */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Corrugated Rolls</Label>
                      <Button size="sm" variant="outline" className="border-[#374151] text-[#9CA3AF] hover:bg-[#2563EB]/10 hover:text-[#60A5FA] hover:border-[#3B82F6]" onClick={() => setProcess2a(prev => ({ ...prev, reels: [...prev.reels, { reel_no: "", reel_size: "", reel_weight: "", reel_product: "Corrugated Roll" }] }))} data-testid="add-corrugated-roll">
                        + Add Roll
                      </Button>
                    </div>
                    {process2a.reels.map((reel, rIdx) => (
                      <div key={rIdx} className="grid grid-cols-12 gap-3 items-end p-3 bg-[#0B0F19] rounded-md border border-[#374151]">
                        <div className="col-span-5">
                          <Label className="text-[#9CA3AF] text-xs">Corrugated Roll #{rIdx + 1}</Label>
                          <Popover open={p2aReelOpen[rIdx] || false} onOpenChange={(open) => setP2aReelOpen(prev => ({ ...prev, [rIdx]: open }))}>
                            <PopoverTrigger asChild>
                              <Button variant="outline" role="combobox" className="w-full justify-between bg-[#111827] border-blue-500/20 text-white mt-1 hover:bg-[#1F2937]">
                                {reel.reel_no || "Select Corrugated Roll..."}
                                <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[400px] p-0 bg-[#111827] border-blue-500/30">
                              <Command className="bg-[#111827]">
                                <CommandInput placeholder="Search corrugated rolls..." value={p2aSearchTerm} onValueChange={setP2aSearchTerm} className="bg-[#111827] text-white" />
                                <CommandList>
                                  <CommandEmpty>No corrugated rolls found.</CommandEmpty>
                                  <CommandGroup className="max-h-60 overflow-auto">
                                    {filteredP2aInventory.filter(inv => inv.product === 'Corrugated Roll').map((inv) => (
                                      <CommandItem key={inv.id} value={`${inv.reel_id} ${inv.rm_name} ${inv.reel_size}`} onSelect={() => {
                                        const selectedReel = availableInventory.find(i => i.reel_id === inv.reel_id);
                                        if (selectedReel) {
                                          const newReels = [...process2a.reels];
                                          newReels[rIdx] = { 
                                            reel_no: selectedReel.reel_id, 
                                            reel_size: selectedReel.reel_size || "", 
                                            reel_weight: selectedReel.qty || "",
                                            reel_product: "Corrugated Roll"
                                          };
                                          setProcess2a(prev => ({ ...prev, reels: newReels }));
                                        }
                                        setP2aReelOpen(prev => ({ ...prev, [rIdx]: false }));
                                      }} className="text-white hover:bg-blue-500/20 cursor-pointer">
                                        <Check className={cn("mr-2 h-4 w-4", reel.reel_no === inv.reel_id ? "opacity-100" : "opacity-0")} />
                                        <span className="flex-1">
                                          {inv.reel_id} - {inv.rm_name} {inv.reel_size ? `(Size: ${inv.reel_size}")` : ""} ({inv.qty} Rolls Available)
                                        </span>
                                        {inv.status === 'stock_used' && (
                                          <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-red-500/20 text-[#9CA3AF] rounded">Used</span>
                                        )}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-blue-300 text-xs">Roll Size</Label>
                          <Input value={reel.reel_size} className="bg-[#111827] border-[#374151] text-[#6B7280] mt-1" readOnly />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[#9CA3AF] text-xs">Available Rolls</Label>
                          <Input type="number" value={reel.reel_weight} onChange={(e) => {
                            const newReels = [...process2a.reels];
                            newReels[rIdx].reel_weight = e.target.value;
                            setProcess2a(prev => ({ ...prev, reels: newReels }));
                          }} className="bg-[#111827] border-[#374151] text-white mt-1" data-testid={`process2a-roll-${rIdx}-qty`} />
                        </div>
                        <div className="col-span-3 flex items-end gap-2">
                          {process2a.reels.length > 1 && (
                            <Button size="sm" variant="ghost" className="text-[#9CA3AF] hover:text-[#9CA3AF] hover:bg-[#2563EB]/10" onClick={() => {
                              setProcess2a(prev => ({ ...prev, reels: prev.reels.filter((_, i) => i !== rIdx) }));
                            }}>
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Sheet Cutting specific fields */}
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-blue-300 font-semibold">Cutting Size</Label><Input value={process2a.cutting_size} onChange={(e) => setProcess2a(prev => ({ ...prev, cutting_size: e.target.value }))} className="bg-[#111827] border-blue-500/20 text-white mt-1" /></div>
                    <div>
                      <Label className="text-blue-300 font-semibold">Sheet Counting</Label>
                      <Input type="number" value={process2a.sheet_counting} onChange={(e) => setProcess2a(prev => ({ ...prev, sheet_counting: e.target.value }))} className="bg-[#111827] border-blue-500/20 text-white mt-1" />
                      {/* Combined hint = P1 estimated sheets + sum of all Corrugated-Roll qty entries */}
                      {(() => {
                        const p1Sheets = (selectedProcesses.process1 && process2a.use_p1_output && p1SheetHint?.estimatedSheets) || 0;
                        const crSheets = (process2a.reels || []).reduce(
                          (acc, r) => acc + (parseInt(r.reel_weight) || 0),
                          0
                        );
                        const combined = p1Sheets + crSheets;
                        if (combined <= 0) return null;
                        return (
                          <div
                            className="mt-2 p-2 bg-blue-500/10 border border-blue-500/30 rounded-md"
                            data-testid="p2a-combined-hint"
                          >
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <span className="text-blue-400 text-xs">
                                {p1Sheets > 0 && (
                                  <>P1: <strong className="text-blue-300">{p1Sheets.toLocaleString()}</strong></>
                                )}
                                {p1Sheets > 0 && crSheets > 0 && <span className="text-gray-500"> + </span>}
                                {crSheets > 0 && (
                                  <>Corrugated Roll: <strong className="text-blue-300">{crSheets.toLocaleString()}</strong></>
                                )}
                                {p1Sheets > 0 && crSheets > 0 && (
                                  <> = <strong className="text-white">{combined.toLocaleString()}</strong></>
                                )}
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 px-2 text-xs bg-blue-600/20 border-blue-500/50 text-blue-300 hover:bg-blue-600/40"
                                onClick={() => setProcess2a(prev => ({ ...prev, sheet_counting: combined.toString() }))}
                                data-testid="use-combined-hint-btn"
                              >
                                Use {p1Sheets > 0 && crSheets > 0 ? "Combined" : "Suggested"}
                              </Button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Use from P1 Corrugation - clean toggle like P3 */}
                  {selectedProcesses.process1 && (
                    <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg" data-testid="use-p1-output-section">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="use-p1-output"
                          checked={process2a.use_p1_output || false}
                          onChange={(e) => setProcess2a(prev => ({ ...prev, use_p1_output: e.target.checked }))}
                          className="w-5 h-5 accent-blue-500"
                          data-testid="use-p1-output-checkbox"
                        />
                        <label htmlFor="use-p1-output" className="text-blue-300 font-semibold">
                          Use from P1 (Corrugation) Output
                        </label>
                      </div>
                      {process2a.use_p1_output && (
                        <div className="mt-3 ml-8 space-y-3">
                          {/* Auto-fetched P1 Reel Data */}
                          {(p1ReelData.baseReels.length > 0 || p1ReelData.fluteReels.length > 0) && (
                            <div className="grid grid-cols-2 gap-3" data-testid="p1-reel-data-display">
                              {p1ReelData.baseReels.length > 0 && (
                                <div className="p-2 bg-[#0B0F19] border border-[#374151] rounded-md">
                                  <span className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wider">Base Reel</span>
                                  {p1ReelData.baseReels.map((r, i) => (
                                    <div key={`base-${r.reel_id || i}`} className="mt-1 text-sm">
                                      <span className="text-white font-medium">{r.reel_id}</span>
                                      <span className="text-[#6B7280] mx-1">|</span>
                                      <span className="text-[#9CA3AF]">{r.rm_name}</span>
                                      <span className="text-[#6B7280] mx-1">|</span>
                                      <span className="text-blue-300 font-medium">GSM: {r.gsm || "N/A"}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {p1ReelData.fluteReels.length > 0 && (
                                <div className="p-2 bg-[#0B0F19] border border-[#374151] rounded-md">
                                  <span className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wider">Flute Reel</span>
                                  {p1ReelData.fluteReels.map((r, i) => (
                                    <div key={`flute-${r.reel_id || i}`} className="mt-1 text-sm">
                                      <span className="text-white font-medium">{r.reel_id}</span>
                                      <span className="text-[#6B7280] mx-1">|</span>
                                      <span className="text-[#9CA3AF]">{r.rm_name}</span>
                                      <span className="text-[#6B7280] mx-1">|</span>
                                      <span className="text-blue-300 font-medium">GSM: {r.gsm || "N/A"}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {p1ReelData.baseReels.length === 0 && p1ReelData.fluteReels.length === 0 && (
                            <p className="text-yellow-400 text-sm">Select Base Reel & Flute Reel in P1 first</p>
                          )}
                          {/* Flute Type & Manage */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-[#9CA3AF] text-xs">Flute Type</Label>
                              <select value={process1.flute_type || ""} onChange={(e) => setProcess1(prev => ({ ...prev, flute_type: e.target.value }))} className="w-full h-10 px-3 rounded-md bg-[#111827] border border-blue-500/20 text-white mt-1 text-sm" data-testid="p1-flute-type-select">
                                <option value="">Select Flute</option>
                                {fluteTypes.map((ft, i) => (
                                  <option key={ft.name || `flute-opt-${i}`} value={ft.name}>{ft.name} ({ft.multiplier}%)</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <Label className="text-[#9CA3AF] text-xs">Manage Flute Types</Label>
                              <Button size="sm" variant="outline" className="w-full mt-1 h-10 border-blue-500/30 text-blue-300 hover:bg-blue-600/20"
                                onClick={() => setProcess2a(prev => ({ ...prev, showFluteManager: !prev.showFluteManager }))}
                                data-testid="toggle-flute-manager"
                              >{process2a.showFluteManager ? "Hide" : "Edit Flute Types"}</Button>
                            </div>
                          </div>
                          {p1SheetHint && p1SheetHint.estimatedSheets > 0 && (
                            <p className="text-blue-400 text-sm">
                              P1 Stock: <span className="text-white font-medium">{p1SheetHint.totalP1Weight} kg</span> | Base GSM: <span className="text-white font-medium">{p1SheetHint.baseGSM}</span> | Flute GSM: <span className="text-white font-medium">{p1SheetHint.fluteGSM}</span> | Total GSM: <span className="text-white font-medium">{p1SheetHint.totalGSM}</span> | Sheet Wt: <span className="text-white font-medium">{p1SheetHint.sheetWeightGrams} gm</span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Flute Type Management - toggle visibility */}
                  {process2a.showFluteManager && (
                    <div className="mt-3 p-3 bg-[#0B0F19] border border-[#374151] rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wider">Flute Types & Multipliers</span>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="h-6 px-2 text-xs border-blue-500/50 text-blue-300 hover:bg-blue-600/20"
                            onClick={() => setFluteTypes(prev => [...prev, { name: "", multiplier: 150, order: prev.length + 1, id: Date.now() }])}
                            data-testid="add-flute-type-btn"
                          >+ Add Flute</Button>
                          <Button size="sm" className="h-6 px-2 text-xs bg-blue-600 text-white hover:bg-blue-700"
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/flute-types`, { types: fluteTypes.map(f => ({ name: f.name, multiplier: f.multiplier })) });
                                toast.success("Flute types saved");
                              } catch (err) { toast.error("Failed to save"); }
                            }}
                            data-testid="save-flute-types-btn"
                          >Save</Button>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {fluteTypes.map((ft, i) => (
                          <div key={ft.id || i} className="grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-5">
                              <input value={ft.name} onChange={(e) => { const n = [...fluteTypes]; n[i] = { ...n[i], name: e.target.value }; setFluteTypes(n); }}
                                className="w-full px-2 py-1 bg-[#111827] border border-[#374151] rounded text-white text-xs" placeholder="Flute name" data-testid={`flute-name-${i}`} />
                            </div>
                            <div className="col-span-4 flex items-center gap-1">
                              <input type="number" value={ft.multiplier} onChange={(e) => { const n = [...fluteTypes]; n[i] = { ...n[i], multiplier: parseFloat(e.target.value) || 0 }; setFluteTypes(n); }}
                                className="w-full px-2 py-1 bg-[#111827] border border-[#374151] rounded text-white text-xs" data-testid={`flute-pct-${i}`} />
                              <span className="text-[#6B7280] text-xs">%</span>
                            </div>
                            <div className="col-span-3">
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                onClick={() => setFluteTypes(prev => prev.filter((_, idx) => idx !== i))}
                                data-testid={`flute-delete-${i}`}
                              >Delete</Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Roll Split Detection - when corrugated roll > item reel size and P5 is selected */}
                  {reelSplitInfo.has_split && selectedProcesses.process5 && (
                    <div className="mt-4 rounded-lg border-2 border-amber-500/50 bg-amber-500/5 overflow-hidden" data-testid="reel-split-section">
                      <div className="px-4 py-3 bg-amber-500/10 border-b border-amber-500/30 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-amber-500 text-black font-bold text-sm">
                          <Scissors className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-bold text-amber-400 text-sm">ROLL SPLIT DETECTED</p>
                          <p className="text-xs text-[#9CA3AF]">
                            Corrugated Roll: <span className="text-white font-medium">{reelSplitInfo.stock_reel_size}"</span> | 
                            Item Needs: <span className="text-white font-medium">{reelSplitInfo.item_reel_size}"</span> | 
                            Remaining: <span className="text-amber-400 font-bold">{reelSplitInfo.remaining_size}"</span>
                          </p>
                          <p className="text-xs text-[#9CA3AF] mt-0.5">
                            Split via 4Bar Rotary Creasing (P5)
                          </p>
                        </div>
                      </div>
                      <div className="p-4 space-y-3">
                        <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">
                          What to do with the remaining {reelSplitInfo.remaining_size}" portion?
                        </Label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {/* Option 1: Assign to existing WO */}
                          <button
                            type="button"
                            onClick={() => setReelSplitInfo(prev => ({ ...prev, handling: "assign_existing_wo" }))}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              reelSplitInfo.handling === "assign_existing_wo" 
                                ? "border-blue-500 bg-blue-500/10" 
                                : "border-[#374151] bg-[#111827] hover:border-[#4B5563]"
                            }`}
                            data-testid="reel-split-assign-existing-wo"
                          >
                            <p className={`font-semibold text-sm ${reelSplitInfo.handling === "assign_existing_wo" ? "text-blue-400" : "text-white"}`}>
                              Assign to Existing Work Order
                            </p>
                            <p className="text-xs text-[#9CA3AF] mt-1">Select an existing WO to use the remaining roll</p>
                          </button>

                          {/* Option 2: Create New Draft WO */}
                          <button
                            type="button"
                            onClick={() => {
                              if (reelSplitInfo.handling !== "new_draft_wo") {
                                createDraftWorkOrder();
                              }
                            }}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              reelSplitInfo.handling === "new_draft_wo" 
                                ? "border-emerald-500 bg-emerald-500/10" 
                                : "border-[#374151] bg-[#111827] hover:border-[#4B5563]"
                            }`}
                            data-testid="reel-split-create-new-wo"
                          >
                            <p className={`font-semibold text-sm ${reelSplitInfo.handling === "new_draft_wo" ? "text-emerald-400" : "text-white"}`}>
                              Create New Work Order (Draft)
                            </p>
                            <p className="text-xs text-[#9CA3AF] mt-1">Auto-create draft WO with {reelSplitInfo.remaining_size}" reel</p>
                          </button>

                          {/* Option 3: Create Bit Roll in Stock */}
                          <button
                            type="button"
                            onClick={() => setReelSplitInfo(prev => ({ ...prev, handling: "bit_roll_stock" }))}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              reelSplitInfo.handling === "bit_roll_stock" 
                                ? "border-purple-500 bg-purple-500/10" 
                                : "border-[#374151] bg-[#111827] hover:border-[#4B5563]"
                            }`}
                            data-testid="reel-split-bit-roll"
                          >
                            <p className={`font-semibold text-sm ${reelSplitInfo.handling === "bit_roll_stock" ? "text-purple-400" : "text-white"}`}>
                              Create Bit Roll in Stock
                            </p>
                            <p className="text-xs text-[#9CA3AF] mt-1">Store remaining {reelSplitInfo.remaining_size}" as bit roll in inventory</p>
                          </button>
                        </div>

                        {/* Assign to existing WO dropdown */}
                        {reelSplitInfo.handling === "assign_existing_wo" && (
                          <div className="mt-2">
                            <Label className="text-[#9CA3AF] text-xs mb-1 block">Select Work Order</Label>
                            <Popover open={woSplitDropdownOpen} onOpenChange={setWoSplitDropdownOpen}>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-between bg-[#111827] border-[#374151] text-white h-9 hover:bg-[#1F2937]">
                                  {reelSplitInfo.assigned_wo || "Select Work Order..."}
                                  <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[400px] p-0 bg-[#111827] border-[#374151]">
                                <Command className="bg-[#111827]">
                                  <CommandInput placeholder="Search WO number..." className="bg-[#111827] text-white" />
                                  <CommandList>
                                    <CommandEmpty>No work orders found.</CommandEmpty>
                                    <CommandGroup className="max-h-60 overflow-auto">
                                      {availableWoList.map((wo) => (
                                        <CommandItem
                                          key={wo.number}
                                          value={`${wo.number} ${wo.customer}`}
                                          onSelect={() => {
                                            setReelSplitInfo(prev => ({ ...prev, assigned_wo: wo.number }));
                                            setWoSplitDropdownOpen(false);
                                          }}
                                          className="text-white hover:bg-slate-500/20 cursor-pointer"
                                        >
                                          <Check className={cn("mr-2 h-4 w-4", reelSplitInfo.assigned_wo === wo.number ? "opacity-100" : "opacity-0")} />
                                          <span className="flex-1">
                                            {wo.number} - {wo.customer} ({wo.item}) {wo.reel_size ? `[${wo.reel_size}"]` : ""}
                                          </span>
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                        )}

                        {/* Draft WO Created Confirmation */}
                        {reelSplitInfo.handling === "new_draft_wo" && reelSplitInfo.assigned_wo && (
                          <div className="mt-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Check className="h-5 w-5 text-emerald-400" />
                              <div>
                                <p className="text-emerald-400 font-semibold text-sm">
                                  Draft Work Order Created: {reelSplitInfo.assigned_wo}
                                </p>
                                <p className="text-xs text-[#9CA3AF] mt-0.5">
                                  Reel Size: {reelSplitInfo.remaining_size}" | Item: {headerData.item_name} | Status: DATA PENDING
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Bit Roll Stock Confirmation */}
                        {reelSplitInfo.handling === "bit_roll_stock" && (
                          <div className="mt-2 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Check className="h-5 w-5 text-purple-400" />
                              <div>
                                <p className="text-purple-400 font-semibold text-sm">
                                  Bit Roll will be created in Stock Inventory
                                </p>
                                <p className="text-xs text-[#9CA3AF] mt-0.5">
                                  Size: {reelSplitInfo.remaining_size}" | Will be added to inventory after work order is saved
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Notes field */}
                        <div className="mt-2">
                          <Label className="text-[#9CA3AF] text-xs mb-1 block">Notes (Optional)</Label>
                          <Input
                            value={reelSplitInfo.notes}
                            onChange={(e) => setReelSplitInfo(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Any additional notes about the roll split..."
                            className="bg-[#111827] border-[#374151] text-white h-9"
                            data-testid="reel-split-notes"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* PROCESS 2B - PAPER CUTTING - Professional Design */}
          {selectedProcesses.process2b && (
            <Card style={{ order: processOrder.indexOf('process2b') >= 0 ? processOrder.indexOf('process2b') : 99 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm">
              <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all py-3" onClick={() => toggleProcess('process2b')}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="bg-[#2563EB] text-white px-3 py-1 rounded-md font-bold text-sm">P2B</span>
                    <CardTitle className="text-lg text-[#F9FAFB] font-semibold">PAPER CUTTING MACHINE</CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-[#111827] border border-[#374151] rounded-md px-3 py-1.5 hover:border-[#3B82F6] transition-all">
                      <Calendar className="h-4 w-4 text-[#9CA3AF]" />
                      <Input type="date" value={process2b.date} onChange={(e) => { e.stopPropagation(); setProcess2b(prev => ({ ...prev, date: e.target.value })); }} className="w-36 bg-transparent border-0 text-white p-0 h-auto focus-visible:ring-0 [color-scheme:dark]" placeholder="Select date" data-testid="process2b-date" />
                    </div>
                    {expandedProcesses.process2b ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                  </div>
                </div>
              </CardHeader>
              {expandedProcesses.process2b && (
                <CardContent className="p-4 bg-[#111827]">
                  {/* Reels Section */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Reels</Label>
                      <Button size="sm" variant="outline" className="border-[#374151] text-[#9CA3AF] hover:bg-[#2563EB]/10 hover:text-[#60A5FA] hover:border-[#3B82F6]" onClick={() => setProcess2b(prev => ({ ...prev, reels: [...prev.reels, { reel_no: "", reel_size: "", reel_weight: "", reel_product: "" }] }))} data-testid="add-paper-reel">
                        + Add Reel
                      </Button>
                    </div>
                    {process2b.reels.map((reel, rIdx) => (
                      <div key={rIdx} className="grid grid-cols-12 gap-3 items-end p-3 bg-[#0B0F19] rounded-md border border-[#374151]">
                        <div className="col-span-5">
                          <Label className="text-[#9CA3AF] text-xs">Reel #{rIdx + 1} - Search & Select</Label>
                          <Popover open={p2bReelOpen[rIdx] || false} onOpenChange={(open) => setP2bReelOpen(prev => ({ ...prev, [rIdx]: open }))}>
                            <PopoverTrigger asChild>
                              <Button variant="outline" role="combobox" className="w-full justify-between bg-[#111827] border-[#374151] text-white mt-1 hover:bg-[#1F2937]">
                                {reel.reel_no || "Search by ID, RM Name, Size..."}
                                <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[400px] p-0 bg-[#111827] border-[#374151]">
                              <Command className="bg-[#111827]">
                                <CommandInput placeholder="Search by ID, RM Name, Size..." value={p2bSearchTerm} onValueChange={setP2bSearchTerm} className="bg-[#111827] text-white" />
                                <CommandList>
                                  <CommandEmpty>No inventory found.</CommandEmpty>
                                  <CommandGroup className="max-h-60 overflow-auto">
                                    {filteredP2bInventory.map((inv) => (
                                      <CommandItem key={inv.id} value={`${inv.reel_id} ${inv.rm_name} ${inv.reel_size}`} onSelect={() => {
                                        const selectedReel = availableInventory.find(i => i.reel_id === inv.reel_id);
                                        if (selectedReel) {
                                          const newReels = [...process2b.reels];
                                          newReels[rIdx] = { 
                                            reel_no: selectedReel.reel_id, 
                                            reel_size: selectedReel.reel_size || "", 
                                            reel_weight: selectedReel.product === 'Corrugated Roll' ? selectedReel.qty : selectedReel.reel_weight || "",
                                            reel_product: selectedReel.product || ""
                                          };
                                          setProcess2b(prev => ({ ...prev, reels: newReels }));
                                        }
                                        setP2bReelOpen(prev => ({ ...prev, [rIdx]: false }));
                                      }} className="text-white hover:bg-cyan-500/20 cursor-pointer">
                                        <Check className={cn("mr-2 h-4 w-4", reel.reel_no === inv.reel_id ? "opacity-100" : "opacity-0")} />
                                        <span className="flex-1">
                                          {inv.product === 'Corrugated Roll' 
                                            ? `${inv.reel_id} - ${inv.rm_name} (${inv.qty} Nos)`
                                            : `${inv.reel_id} - ${inv.rm_name} (${inv.reel_size}" / ${inv.reel_weight}kg)`
                                          }
                                        </span>
                                        {inv.status === 'stock_used' && (
                                          <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-red-500/20 text-[#9CA3AF] rounded">Used</span>
                                        )}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[#9CA3AF] text-xs">Reel Size (Auto)</Label>
                          <Input value={reel.reel_size} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[#9CA3AF] text-xs">
                            {reel.reel_product === 'Corrugated Roll' ? 'Qty (Nos)' : 'Reel Weight (kg)'}
                          </Label>
                          <Input type="number" value={reel.reel_weight} onChange={(e) => {
                            const newReels = [...process2b.reels];
                            newReels[rIdx].reel_weight = e.target.value;
                            setProcess2b(prev => ({ ...prev, reels: newReels }));
                          }} className="bg-[#111827] border-[#374151] text-white mt-1" />
                        </div>
                        <div className="col-span-3 flex items-end gap-2">
                          {process2b.reels.length > 1 && (
                            <Button size="sm" variant="ghost" className="text-[#9CA3AF] hover:text-[#9CA3AF] hover:bg-[#2563EB]/10" onClick={() => {
                              setProcess2b(prev => ({ ...prev, reels: prev.reels.filter((_, i) => i !== rIdx) }));
                            }}>
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Paper Cutting specific fields */}
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-[#9CA3AF] font-semibold">Cutting Size</Label><Input value={process2b.cutting_size} onChange={(e) => setProcess2b(prev => ({ ...prev, cutting_size: e.target.value }))} className="bg-[#111827] border-[#374151] text-white mt-1" /></div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold">Paper Counting</Label>
                      <div className="flex gap-2 mt-1">
                        <Input 
                          type="number" 
                          value={process2b.paper_counting} 
                          onChange={(e) => setProcess2b(prev => ({ ...prev, paper_counting: e.target.value }))} 
                          className="bg-[#111827] border-[#374151] text-white flex-1" 
                          data-testid="paper-counting-input"
                        />
                      </div>
                      {/* Hint with calculated value and quick increment buttons */}
                      {paperCountHint > 0 && process2b.reels.some(r => r.reel_no) && (
                        <div className="mt-2 p-2 bg-cyan-500/10 border border-cyan-500/30 rounded-md">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-cyan-400 text-xs">
                              Suggested: <strong className="text-cyan-300">{paperCountHint}</strong> (based on {process3.no_of_ply || 3} PLY)
                            </span>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-6 px-2 text-xs bg-cyan-600/20 border-cyan-500/50 text-cyan-300 hover:bg-cyan-600/40"
                              onClick={() => setProcess2b(prev => ({ ...prev, paper_counting: paperCountHint.toString() }))}
                              data-testid="use-hint-btn"
                            >
                              Use Hint
                            </Button>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="flex-1 h-7 text-xs bg-[#111827] border-[#374151] text-[#9CA3AF] hover:bg-green-600/20 hover:border-green-500 hover:text-green-300"
                              onClick={() => setProcess2b(prev => ({ ...prev, paper_counting: (parseInt(prev.paper_counting || 0) + 10).toString() }))}
                              data-testid="increment-10-btn"
                            >
                              +10
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="flex-1 h-7 text-xs bg-[#111827] border-[#374151] text-[#9CA3AF] hover:bg-green-600/20 hover:border-green-500 hover:text-green-300"
                              onClick={() => setProcess2b(prev => ({ ...prev, paper_counting: (parseInt(prev.paper_counting || 0) + 20).toString() }))}
                              data-testid="increment-20-btn"
                            >
                              +20
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="flex-1 h-7 text-xs bg-[#111827] border-[#374151] text-[#9CA3AF] hover:bg-green-600/20 hover:border-green-500 hover:text-green-300"
                              onClick={() => setProcess2b(prev => ({ ...prev, paper_counting: (parseInt(prev.paper_counting || 0) + 30).toString() }))}
                              data-testid="increment-30-btn"
                            >
                              +30
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Top Paper Weight — flows into Inventory as 'Top Paper' stock on save */}
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <Label className="text-emerald-300 font-semibold">Top Paper Weight (Kg)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={process2b.top_paper_weight}
                        onChange={(e) => setProcess2b(prev => ({ ...prev, top_paper_weight: e.target.value }))}
                        placeholder="e.g., 15"
                        className="bg-[#111827] border-emerald-500/40 text-white mt-1"
                        data-testid="top-paper-weight-input"
                      />
                      <p className="text-[10px] text-emerald-300/70 mt-1">
                        Cut output is auto-saved to Inventory as a new <strong>Top Paper</strong> stock (TPCPIxxx) when this WO is saved.
                      </p>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* PROCESS 3 - Professional Design */}
          {selectedProcesses.process3 && (
            <Card style={{ order: processOrder.indexOf('process3') >= 0 ? processOrder.indexOf('process3') : 99 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm">
              <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all py-3" onClick={() => toggleProcess('process3')}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="bg-[#2563EB] text-white px-3 py-1 rounded-md font-bold text-sm">P3</span>
                    <CardTitle className="text-lg text-[#F9FAFB] font-semibold">SHEET PASTING MACHINE</CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-[#111827] border border-[#374151] rounded-md px-3 py-1.5 hover:border-[#3B82F6] transition-all">
                      <Calendar className="h-4 w-4 text-[#9CA3AF]" />
                      <Input type="date" value={process3.date} onChange={(e) => { e.stopPropagation(); setProcess3(prev => ({ ...prev, date: e.target.value })); }} className="w-36 bg-transparent border-0 text-white p-0 h-auto focus-visible:ring-0 [color-scheme:dark]" placeholder="Select date" data-testid="process3-date" />
                    </div>
                    {expandedProcesses.process3 ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                  </div>
                </div>
              </CardHeader>
              {expandedProcesses.process3 && (
                <CardContent className="p-4 bg-[#111827]">
                  {/* Option to use P2B Output */}
                  {selectedProcesses.process2b && (
                    <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="use-p2b-output"
                          checked={process3.use_p2b_output}
                          onChange={(e) => setProcess3(prev => ({ ...prev, use_p2b_output: e.target.checked }))}
                          className="w-5 h-5 accent-emerald-500"
                        />
                        <label htmlFor="use-p2b-output" className="text-emerald-300 font-semibold">
                          Use Paper from P2B (Paper Cutting) Output
                        </label>
                      </div>
                      {process3.use_p2b_output && process2b.paper_counting && (
                        <p className="text-emerald-400 text-sm mt-2 ml-8">
                          Linked to P2B output: {process2b.paper_counting} papers
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">PLY</Label>
                      <Select value={process3.no_of_ply} onValueChange={(val) => setProcess3(prev => ({ ...prev, no_of_ply: val }))}>
                        <SelectTrigger className="bg-[#111827] border-[#374151] text-white mt-1" data-testid="process3-ply">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['3', '5', '7', '9', '11'].map(p => <SelectItem key={p} value={p}>{p} PLY</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Show Top Paper Type only if not using P2B output */}
                    {!(selectedProcesses.process2b && process3.use_p2b_output) && (
                      <div>
                        <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Top Paper Type</Label>
                        <Select value={process3.top_paper_type} onValueChange={(val) => setProcess3(prev => ({ ...prev, top_paper_type: val, stock_id: "" }))}>
                          <SelectTrigger className="bg-[#111827] border-[#374151] text-white mt-1" data-testid="process3-paper-type">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {productTypes.length > 0 
                              ? productTypes.map(pt => <SelectItem key={pt.id} value={pt.name}>{pt.name}</SelectItem>)
                              : ['Plain Kraft', 'Plain Duplex', 'Plain Gyt', 'Plain Imported', 'Printed Kraft', 'Printed Duplex', 'Printed Gyt', 'Printed Imported'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)
                            }
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {/* Show Stock ID only if not using P2B output */}
                    {!(selectedProcesses.process2b && process3.use_p2b_output) && (
                      <div>
                        <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Stock ID</Label>
                        <Popover open={p3StockOpen} onOpenChange={setP3StockOpen}>
                          <PopoverTrigger asChild>
                            <Button 
                              variant="outline" 
                              role="combobox" 
                              className="w-full justify-between bg-[#111827] border-[#374151] text-white mt-1 hover:bg-[#1F2937]"
                              disabled={!process3.top_paper_type}
                            >
                              {process3.stock_id || (process3.top_paper_type ? "Select Stock ID..." : "Select Paper Type first")}
                              <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[400px] p-0 bg-[#111827] border-[#374151]">
                            <Command className="bg-[#111827]">
                              <CommandInput placeholder="Search Stock ID..." value={p3SearchTerm} onValueChange={setP3SearchTerm} className="bg-[#111827] text-white" />
                              <CommandList>
                                <CommandEmpty>
                                  {process3.top_paper_type 
                                    ? `No ${process3.top_paper_type} items in inventory` 
                                    : "No inventory found."}
                                </CommandEmpty>
                                <CommandGroup className="max-h-60 overflow-auto">
                                  {availableInventory
                                    .filter(inv => {
                                      // Filter by selected paper type
                                      if (process3.top_paper_type && inv.product?.toLowerCase() !== process3.top_paper_type.toLowerCase()) {
                                        return false;
                                      }
                                      if (!p3SearchTerm) return true;
                                      const term = p3SearchTerm.toLowerCase();
                                      return (
                                        inv.reel_id?.toLowerCase().includes(term) ||
                                        inv.rm_name?.toLowerCase().includes(term) ||
                                        inv.reel_size?.toString().includes(term)
                                      );
                                    })
                                    .map((inv) => (
                                    <CommandItem
                                      key={inv.id}
                                      value={`${inv.reel_id} ${inv.rm_name} ${inv.reel_size || ""}`}
                                      onSelect={() => handleP3StockSelect(inv.reel_id)}
                                      className="text-white hover:bg-slate-500/20 cursor-pointer"
                                    >
                                      <Check className={cn("mr-2 h-4 w-4", process3.stock_id === inv.reel_id ? "opacity-100" : "opacity-0")} />
                                      <div className="flex flex-col">
                                        <span>{inv.reel_id} - {inv.rm_name}</span>
                                        <span className="text-xs text-emerald-400 font-semibold">
                                          Available: {inv.qty || 0} {inv.unit || 'Nos'} | Size: {inv.reel_size || 'N/A'}"
                                        </span>
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        {/* Show Available Qty popup after selection */}
                        {process3.stock_id && (() => {
                          const selectedStock = availableInventory.find(inv => inv.reel_id === process3.stock_id);
                          return selectedStock ? (
                            <div className="mt-2 p-3 bg-emerald-500/20 border border-emerald-500/50 rounded-lg animate-pulse">
                              <div className="flex items-center justify-between">
                                <span className="text-emerald-300 text-sm font-medium">Available Qty:</span>
                                <span className="text-emerald-100 text-lg font-bold">
                                  {selectedStock.qty || 0} {selectedStock.unit || 'Nos'}
                                </span>
                              </div>
                              <div className="text-xs text-emerald-400 mt-1">
                                Product: {selectedStock.product || 'N/A'} | RM: {selectedStock.rm_name || 'N/A'}
                              </div>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}
                    
                    {/* If using P2B output, show placeholder info */}
                    {selectedProcesses.process2b && process3.use_p2b_output && (
                      <div className="col-span-2">
                        <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Paper Source</Label>
                        <div className="mt-1 px-4 py-3 bg-emerald-900/30 border border-emerald-500/50 rounded-md">
                          <span className="text-emerald-300 font-medium">Using P2B Paper Cutting Output</span>
                          {process2b.reels?.length > 0 && (
                            <p className="text-emerald-400/70 text-xs mt-1">
                              Reels: {process2b.reels.map(r => r.reel_no).filter(Boolean).join(', ') || 'Not assigned yet'}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Reel Size</Label><Input value={process3.reel_size} onChange={(e) => setProcess3(prev => ({ ...prev, reel_size: e.target.value }))} className="bg-[#111827] border-[#374151] text-white mt-1" /></div>
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Cutting Size</Label><Input value={process3.cutting_size} onChange={(e) => setProcess3(prev => ({ ...prev, cutting_size: e.target.value }))} className="bg-[#111827] border-[#374151] text-white mt-1" /></div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Pasting Board Counting</Label>
                      <Input 
                        type="number" 
                        value={process3.sheet_counting} 
                        onChange={(e) => setProcess3(prev => ({ ...prev, sheet_counting: e.target.value }))} 
                        className="bg-[#111827] border-[#374151] text-white mt-1" 
                        data-testid="pasting-board-counting-input"
                      />
                      {/* Hint with calculated pasting board count */}
                      {pastingBoardHint > 0 && (
                        <div className="mt-2 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-md">
                          <div className="flex items-center justify-between">
                            <span className="text-emerald-400 text-xs">
                              Suggested: <strong className="text-emerald-300">{pastingBoardHint}</strong> (from Paper Count)
                            </span>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-6 px-2 text-xs bg-emerald-600/20 border-emerald-500/50 text-emerald-300 hover:bg-emerald-600/40"
                              onClick={() => setProcess3(prev => ({ ...prev, sheet_counting: pastingBoardHint.toString() }))}
                              data-testid="use-pasting-hint-btn"
                            >
                              Use Hint
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* PROCESS 4 */}
          {selectedProcesses.process4 && (
            <Card style={{ order: processOrder.indexOf('process4') >= 0 ? processOrder.indexOf('process4') : 99 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm transition-all">
              <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all py-3" onClick={() => toggleProcess('process4')}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="bg-[#2563EB] text-white px-3 py-1 rounded-md font-bold text-sm">P4</span>
                    <CardTitle className="text-lg text-[#F9FAFB] font-semibold">THIN BLADE SLITTER SCORER MACHINE</CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-[#111827] border border-[#374151] rounded-md px-3 py-1.5 hover:border-[#3B82F6] transition-all">
                      <Calendar className="h-4 w-4 text-[#9CA3AF]" />
                      <Input type="date" value={process4.date} onChange={(e) => { e.stopPropagation(); setProcess4(prev => ({ ...prev, date: e.target.value })); }} className="w-36 bg-transparent border-0 text-white p-0 h-auto focus-visible:ring-0 [color-scheme:dark]" placeholder="Select date" />
                    </div>
                    {expandedProcesses.process4 ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                  </div>
                </div>
              </CardHeader>
              {expandedProcesses.process4 && (
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Length (Auto)</Label><Input value={process4.length} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly /></div>
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Breadth (Auto)</Label><Input value={process4.breadth} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly /></div>
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Height (Auto)</Label><Input value={process4.height} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly /></div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Board Counting</Label>
                      <Input type="number" value={process4.board_counting} onChange={(e) => setProcess4(prev => ({ ...prev, board_counting: e.target.value }))} className="bg-[#111827] border-[#374151] text-white mt-1" />
                      {process3.sheet_counting && headerData.ups && (
                        <p className="text-xs text-amber-400 mt-1">
                          P3 Pasting ({process3.sheet_counting}) x UPS ({headerData.ups}) = <span className="font-bold">{parseInt(process3.sheet_counting || 0) * parseInt(String(headerData.ups).replace(/\D/g, '') || 1)}</span> Nos
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* PROCESS 5 */}
          {selectedProcesses.process5 && (
            <Card style={{ order: processOrder.indexOf('process5') >= 0 ? processOrder.indexOf('process5') : 99 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm transition-all">
              <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all py-3" onClick={() => toggleProcess('process5')}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="bg-[#2563EB] text-white px-3 py-1 rounded-md font-bold text-sm">P5</span>
                    <CardTitle className="text-lg text-[#F9FAFB] font-semibold">4 BAR ROTARY CREASING MACHINE</CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-[#111827] border border-[#374151] rounded-md px-3 py-1.5 hover:border-[#3B82F6] transition-all">
                      <Calendar className="h-4 w-4 text-[#9CA3AF]" />
                      <Input type="date" value={process5.date} onChange={(e) => { e.stopPropagation(); setProcess5(prev => ({ ...prev, date: e.target.value })); }} className="w-36 bg-transparent border-0 text-white p-0 h-auto focus-visible:ring-0 [color-scheme:dark]" placeholder="Select date" />
                    </div>
                    {expandedProcesses.process5 ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                  </div>
                </div>
              </CardHeader>
              {expandedProcesses.process5 && (
                <CardContent className="p-4 space-y-4">
                  {/* Corrugated Roll Selection */}
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                    <Label className="text-amber-400 font-semibold text-xs uppercase tracking-wider mb-2 block">Select Corrugated Roll (Optional)</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-between bg-[#111827] border-[#374151] text-white h-9 hover:bg-[#1F2937]" data-testid="p5-corrugated-roll-select">
                          {process5.corrugated_roll_id ? `${process5.corrugated_roll_id} (${process5.corrugated_roll_size || '?'}")` : "Select Corrugated Roll..."}
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0 bg-[#111827] border-[#374151]">
                        <Command className="bg-[#111827]">
                          <CommandInput placeholder="Search corrugated roll..." className="bg-[#111827] text-white" />
                          <CommandList>
                            <CommandEmpty>No corrugated rolls found.</CommandEmpty>
                            <CommandGroup className="max-h-60 overflow-auto">
                              {availableInventory
                                .filter(inv => inv.product === 'Corrugated Roll' && inv.status === 'available')
                                .map((inv) => (
                                  <CommandItem
                                    key={inv.reel_id}
                                    value={`${inv.reel_id} ${inv.rm_name}`}
                                    onSelect={() => {
                                      setProcess5(prev => ({
                                        ...prev,
                                        corrugated_roll_id: inv.reel_id,
                                        corrugated_roll_size: inv.reel_size?.toString() || "",
                                        corrugated_roll_weight: inv.reel_weight?.toString() || ""
                                      }));
                                    }}
                                    className="text-white hover:bg-slate-500/20 cursor-pointer"
                                  >
                                    <Check className={cn("mr-2 h-4 w-4", process5.corrugated_roll_id === inv.reel_id ? "opacity-100" : "opacity-0")} />
                                    <span className="flex-1">
                                      {inv.reel_id} — {inv.rm_name} {inv.reel_size ? `[${inv.reel_size}"]` : ""} {inv.reel_weight ? `${inv.reel_weight}kg` : ""}
                                    </span>
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {process5.corrugated_roll_id && (
                      <div className="mt-2 flex items-center gap-4 text-sm">
                        <span className="text-[#9CA3AF]">Roll ID: <span className="text-amber-400 font-mono">{process5.corrugated_roll_id}</span></span>
                        {process5.corrugated_roll_size && <span className="text-[#9CA3AF]">Size: <span className="text-white font-medium">{process5.corrugated_roll_size}"</span></span>}
                        {process5.corrugated_roll_weight && <span className="text-[#9CA3AF]">Weight: <span className="text-white font-medium">{process5.corrugated_roll_weight} Kg</span></span>}
                        <button type="button" onClick={() => setProcess5(prev => ({ ...prev, corrugated_roll_id: "", corrugated_roll_size: "", corrugated_roll_weight: "" }))} className="text-red-400 hover:text-red-300 text-xs ml-auto">Clear</button>
                      </div>
                    )}
                  </div>

                  {/* Roll Split Detection - shows when corrugated roll > item reel size */}
                  {reelSplitInfo.has_split && (
                    <div className="rounded-lg border-2 border-amber-500/50 bg-amber-500/5 overflow-hidden" data-testid="p5-reel-split-section">
                      <div className="px-4 py-3 bg-amber-500/10 border-b border-amber-500/30 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-amber-500 text-black font-bold text-sm">
                          <Scissors className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-bold text-amber-400 text-sm">ROLL SPLIT DETECTED</p>
                          <p className="text-xs text-[#9CA3AF]">
                            Roll: <span className="text-white font-medium">{reelSplitInfo.stock_reel_size}"</span> | 
                            Item Needs: <span className="text-white font-medium">{reelSplitInfo.item_reel_size}"</span> | 
                            Balance: <span className="text-amber-400 font-bold">{reelSplitInfo.remaining_size}"</span>
                          </p>
                        </div>
                      </div>
                      <div className="p-4 space-y-3">
                        <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">
                          What to do with the balance {reelSplitInfo.remaining_size}" portion?
                        </Label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <button type="button" onClick={() => setReelSplitInfo(prev => ({ ...prev, handling: "assign_existing_wo" }))}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${reelSplitInfo.handling === "assign_existing_wo" ? "border-blue-500 bg-blue-500/10" : "border-[#374151] bg-[#111827] hover:border-[#4B5563]"}`}
                            data-testid="p5-split-assign-wo">
                            <p className={`font-semibold text-sm ${reelSplitInfo.handling === "assign_existing_wo" ? "text-blue-400" : "text-white"}`}>Assign to Existing WO</p>
                            <p className="text-xs text-[#9CA3AF] mt-1">Use remaining in an existing work order</p>
                          </button>
                          <button type="button" onClick={() => { if (reelSplitInfo.handling !== "new_draft_wo") createDraftWorkOrder(); }}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${reelSplitInfo.handling === "new_draft_wo" ? "border-emerald-500 bg-emerald-500/10" : "border-[#374151] bg-[#111827] hover:border-[#4B5563]"}`}
                            data-testid="p5-split-new-wo">
                            <p className={`font-semibold text-sm ${reelSplitInfo.handling === "new_draft_wo" ? "text-emerald-400" : "text-white"}`}>Create New Draft WO</p>
                            <p className="text-xs text-[#9CA3AF] mt-1">Auto-create draft WO with {reelSplitInfo.remaining_size}" reel</p>
                          </button>
                          <button type="button" onClick={() => setReelSplitInfo(prev => ({ ...prev, handling: "bit_roll_stock" }))}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${reelSplitInfo.handling === "bit_roll_stock" ? "border-purple-500 bg-purple-500/10" : "border-[#374151] bg-[#111827] hover:border-[#4B5563]"}`}
                            data-testid="p5-split-bit-roll">
                            <p className={`font-semibold text-sm ${reelSplitInfo.handling === "bit_roll_stock" ? "text-purple-400" : "text-white"}`}>Create Bit Roll in Stock</p>
                            <p className="text-xs text-[#9CA3AF] mt-1">Store remaining as bit roll in inventory</p>
                          </button>
                        </div>
                        {reelSplitInfo.handling === "assign_existing_wo" && (
                          <div className="mt-2">
                            <Label className="text-[#9CA3AF] text-xs mb-1 block">Select Work Order</Label>
                            <Popover open={woSplitDropdownOpen} onOpenChange={setWoSplitDropdownOpen}>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-between bg-[#111827] border-[#374151] text-white h-9 hover:bg-[#1F2937]">
                                  {reelSplitInfo.assigned_wo || "Select Work Order..."} <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[400px] p-0 bg-[#111827] border-[#374151]">
                                <Command className="bg-[#111827]">
                                  <CommandInput placeholder="Search WO number..." className="bg-[#111827] text-white" />
                                  <CommandList>
                                    <CommandEmpty>No work orders found.</CommandEmpty>
                                    <CommandGroup className="max-h-60 overflow-auto">
                                      {availableWoList.map((wo) => (
                                        <CommandItem key={wo.number} value={`${wo.number} ${wo.customer}`} onSelect={() => { setReelSplitInfo(prev => ({ ...prev, assigned_wo: wo.number })); setWoSplitDropdownOpen(false); }} className="text-white hover:bg-slate-500/20 cursor-pointer">
                                          <Check className={cn("mr-2 h-4 w-4", reelSplitInfo.assigned_wo === wo.number ? "opacity-100" : "opacity-0")} />
                                          {wo.number} - {wo.customer} ({wo.item}) {wo.reel_size ? `[${wo.reel_size}"]` : ""}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                        )}
                        {reelSplitInfo.handling === "new_draft_wo" && reelSplitInfo.assigned_wo && (
                          <div className="mt-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Check className="h-5 w-5 text-emerald-400" />
                              <div>
                                <p className="text-emerald-400 font-semibold text-sm">Draft WO Created: {reelSplitInfo.assigned_wo}</p>
                                <p className="text-xs text-[#9CA3AF] mt-0.5">Reel Size: {reelSplitInfo.remaining_size}" | Status: DATA PENDING</p>
                              </div>
                            </div>
                          </div>
                        )}
                        {reelSplitInfo.handling === "bit_roll_stock" && (
                          <div className="mt-2 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Check className="h-5 w-5 text-purple-400" />
                              <div>
                                <p className="text-purple-400 font-semibold text-sm">Bit Roll will be created in Stock</p>
                                <p className="text-xs text-[#9CA3AF] mt-0.5">Size: {reelSplitInfo.remaining_size}" | Added to inventory after saving</p>
                              </div>
                            </div>
                          </div>
                        )}
                        <div>
                          <Label className="text-[#9CA3AF] text-xs mb-1 block">Notes (Optional)</Label>
                          <Input value={reelSplitInfo.notes} onChange={(e) => setReelSplitInfo(prev => ({ ...prev, notes: e.target.value }))} placeholder="Any notes about the roll split..." className="bg-[#111827] border-[#374151] text-white h-9" data-testid="p5-split-notes" />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Length (Auto)</Label><Input value={process5.length} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly /></div>
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Breadth (Auto)</Label><Input value={process5.breadth} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly /></div>
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Height (Auto)</Label><Input value={process5.height} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly /></div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Printing</Label>
                      <Select value={process5.printing} onValueChange={(val) => setProcess5(prev => ({ ...prev, printing: val }))}>
                        <SelectTrigger className="bg-[#111827] border-[#374151] text-white mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">YES</SelectItem>
                          <SelectItem value="no">NO</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Color</Label>
                      <Select value={process5.color} onValueChange={(val) => setProcess5(prev => ({ ...prev, color: val }))}>
                        <SelectTrigger className="bg-[#111827] border-[#374151] text-white mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {['Blue', 'Yellow', 'Red', 'Black', 'Brown', 'Green'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Board Counting</Label><Input type="number" value={process5.board_counting} onChange={(e) => setProcess5(prev => ({ ...prev, board_counting: e.target.value }))} className="bg-[#111827] border-[#374151] text-white mt-1" /></div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* PROCESS 6 */}
          {selectedProcesses.process6 && (
            <Card style={{ order: processOrder.indexOf('process6') >= 0 ? processOrder.indexOf('process6') : 99 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm transition-all">
              <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all py-3" onClick={() => toggleProcess('process6')}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="bg-[#2563EB] text-white px-3 py-1 rounded-md font-bold text-sm">P6</span>
                    <CardTitle className="text-lg text-[#F9FAFB] font-semibold">RS4 SINGLE COLOR PRINTING & SLOTTING MACHINE</CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-[#111827] border border-[#374151] rounded-md px-3 py-1.5 hover:border-[#3B82F6] transition-all">
                      <Calendar className="h-4 w-4 text-[#9CA3AF]" />
                      <Input type="date" value={process6.date} onChange={(e) => { e.stopPropagation(); setProcess6(prev => ({ ...prev, date: e.target.value })); }} className="w-36 bg-transparent border-0 text-white p-0 h-auto focus-visible:ring-0 [color-scheme:dark]" placeholder="Select date" />
                    </div>
                    {expandedProcesses.process6 ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                  </div>
                </div>
              </CardHeader>
              {expandedProcesses.process6 && (
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Length (Auto)</Label><Input value={process6.length} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly /></div>
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Breadth (Auto)</Label><Input value={process6.breadth} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly /></div>
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Height (Auto)</Label><Input value={process6.height} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly /></div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Printing</Label>
                      <Select value={process6.printing} onValueChange={(val) => setProcess6(prev => ({ ...prev, printing: val }))}>
                        <SelectTrigger className="bg-[#111827] border-[#374151] text-white mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="No">No</SelectItem>
                          <SelectItem value="Yes">Yes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Color</Label>
                      <Select value={process6.color || "select"} onValueChange={(val) => setProcess6(prev => ({ ...prev, color: val === "select" ? "" : val }))}>
                        <SelectTrigger className="bg-[#111827] border-[#374151] text-white mt-1">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="select">Select</SelectItem>
                          <SelectItem value="Blue">Blue</SelectItem>
                          <SelectItem value="Black">Black</SelectItem>
                          <SelectItem value="Green">Green</SelectItem>
                          <SelectItem value="Red">Red</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Board Counting</Label>
                      <Input 
                        type="number" 
                        value={process6.board_counting} 
                        onChange={(e) => setProcess6(prev => ({ ...prev, board_counting: e.target.value }))} 
                        className="bg-[#111827] border-[#374151] text-white mt-1" 
                        placeholder="Enter count"
                      />
                      {process4.board_counting && (
                        <p className="text-xs text-amber-400 mt-1">
                          Hint: Thin Blade Counting = <span className="font-bold">{process4.board_counting}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* PROCESS 6A - Eccentric Slotter (added Feb 2026) */}
          {selectedProcesses.process6a && (
            <Card style={{ order: processOrder.indexOf('process6a') >= 0 ? processOrder.indexOf('process6a') : 99 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm transition-all">
              <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all py-3" onClick={() => toggleProcess('process6a')}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="bg-[#2563EB] text-white px-3 py-1 rounded-md font-bold text-sm">P6A</span>
                    <CardTitle className="text-lg text-[#F9FAFB] font-semibold">ECCENTRIC SLOTTER</CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-[#111827] border border-[#374151] rounded-md px-3 py-1.5 hover:border-[#3B82F6] transition-all">
                      <Calendar className="h-4 w-4 text-[#9CA3AF]" />
                      <Input type="date" value={process6a.date} onChange={(e) => { e.stopPropagation(); setProcess6a(prev => ({ ...prev, date: e.target.value })); }} className="w-36 bg-transparent border-0 text-white p-0 h-auto focus-visible:ring-0 [color-scheme:dark]" placeholder="Select date" />
                    </div>
                    {expandedProcesses.process6a ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                  </div>
                </div>
              </CardHeader>
              {expandedProcesses.process6a && (
                <CardContent className="p-4">
                  <div className="grid grid-cols-4 gap-3">
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Length (Auto)</Label><Input value={process6a.length} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly data-testid="p6a-length" /></div>
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Breadth (Auto)</Label><Input value={process6a.breadth} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly data-testid="p6a-breadth" /></div>
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Height (Auto)</Label><Input value={process6a.height} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly data-testid="p6a-height" /></div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Board Counting</Label>
                      <Input
                        type="number"
                        value={process6a.board_counting}
                        onChange={(e) => setProcess6a(prev => ({ ...prev, board_counting: e.target.value }))}
                        className="bg-[#111827] border-[#374151] text-white mt-1"
                        placeholder="Enter count"
                        data-testid="p6a-board-counting"
                      />
                      {process6.board_counting && (
                        <p className="text-xs text-amber-400 mt-1">
                          Hint: RS4 Counting = <span className="font-bold">{process6.board_counting}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* PROCESS 7 */}
          {selectedProcesses.process7 && (
            <Card style={{ order: processOrder.indexOf('process7') >= 0 ? processOrder.indexOf('process7') : 99 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm transition-all">
              <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all py-3" onClick={() => toggleProcess('process7')}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="bg-[#2563EB] text-white px-3 py-1 rounded-md font-bold text-sm">P7</span>
                    <CardTitle className="text-lg text-[#F9FAFB] font-semibold">FLAT BED PUNCHING</CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-[#111827] border border-[#374151] rounded-md px-3 py-1.5 hover:border-[#3B82F6] transition-all">
                      <Calendar className="h-4 w-4 text-[#9CA3AF]" />
                      <Input type="date" value={process7.date} onChange={(e) => { e.stopPropagation(); setProcess7(prev => ({ ...prev, date: e.target.value })); }} className="w-36 bg-transparent border-0 text-white p-0 h-auto focus-visible:ring-0 [color-scheme:dark]" placeholder="Select date" />
                    </div>
                    {expandedProcesses.process7 ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                  </div>
                </div>
              </CardHeader>
              {expandedProcesses.process7 && (
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Item Name (Auto)</Label><Input value={process7.item_name} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly /></div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Number of Dies</Label>
                      <Input type="number" min="1" value={process7.die_ids?.length > 0 ? process7.die_ids.length : process7.number_of_dies} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly data-testid="p7-number-of-dies" />
                    </div>
                    {(!process7.die_ids || process7.die_ids.length === 0) && process7.die_id && (
                      <div>
                        <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Die ID (Auto)</Label>
                        <Input value={process7.die_id} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly data-testid="p7-die-id" />
                        <p className="text-xs text-amber-400 mt-1">Auto-populated from Prod & Material Info</p>
                      </div>
                    )}
                    {(!process7.die_ids || process7.die_ids.length === 0) && (
                      <div>
                        <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Counting</Label>
                        <Input type="number" value={process7.counting} onChange={(e) => setProcess7(prev => ({ ...prev, counting: e.target.value }))} className="bg-[#111827] border-[#374151] text-white mt-1" data-testid="p7-counting" />
                        {process3.sheet_counting && headerData.ups && (
                          <p className="text-xs text-amber-400 mt-1">
                            P3 Pasting ({process3.sheet_counting}) x UPS ({headerData.ups}) = <span className="font-bold">{parseInt(process3.sheet_counting || 0) * parseInt(String(headerData.ups).replace(/\D/g, '') || 1)}</span> Nos
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  {process7.die_ids?.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Die IDs & Counting (Auto-populated)</Label>
                      <p className="text-xs text-amber-400">Auto-populated from Prod & Material Info. Enter counting for each die separately.</p>
                      {process3.sheet_counting && headerData.ups && (
                        <p className="text-xs text-cyan-400">
                          Total expected: P3 Pasting ({process3.sheet_counting}) x UPS ({headerData.ups}) = <span className="font-bold">{parseInt(process3.sheet_counting || 0) * parseInt(String(headerData.ups).replace(/\D/g, '') || 1)}</span> Nos
                        </p>
                      )}
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        {process7.die_ids.map((did, idx) => (
                          <div key={did || idx} className="flex items-center gap-2 bg-[#0F172A] border border-[#374151] rounded-md p-2">
                            <span className="text-blue-400 font-mono text-sm min-w-[80px]">{did}</span>
                            <Input
                              type="number"
                              value={process7.die_countings?.[did] || ""}
                              onChange={(e) => setProcess7(prev => ({
                                ...prev,
                                die_countings: { ...prev.die_countings, [did]: e.target.value }
                              }))}
                              className="bg-[#111827] border-[#374151] text-white flex-1"
                              placeholder="Counting"
                              data-testid={`p7-counting-${did}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* PROCESS 8 */}
          {selectedProcesses.process8 && (
            <Card style={{ order: processOrder.indexOf('process8') >= 0 ? processOrder.indexOf('process8') : 99 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm transition-all">
              <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all py-3" onClick={() => toggleProcess('process8')}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="bg-[#2563EB] text-white px-3 py-1 rounded-md font-bold text-sm">P8</span>
                    <CardTitle className="text-lg text-[#F9FAFB] font-semibold">ROTARY DIE PUNCHING - SINGLE COLOR PRINT</CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-[#111827] border border-[#374151] rounded-md px-3 py-1.5 hover:border-[#3B82F6] transition-all">
                      <Calendar className="h-4 w-4 text-[#9CA3AF]" />
                      <Input type="date" value={process8.date} onChange={(e) => { e.stopPropagation(); setProcess8(prev => ({ ...prev, date: e.target.value })); }} className="w-36 bg-transparent border-0 text-white p-0 h-auto focus-visible:ring-0 [color-scheme:dark]" placeholder="Select date" />
                    </div>
                    {expandedProcesses.process8 ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                  </div>
                </div>
              </CardHeader>
              {expandedProcesses.process8 && (
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Number of Dies</Label>
                      <Input type="number" min="1" value={process8.die_ids?.length > 0 ? process8.die_ids.length : 1} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly data-testid="p8-number-of-dies" />
                    </div>
                    {(!process8.die_ids || process8.die_ids.length === 0) && process8.die_id && (
                      <div>
                        <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Die ID (Auto)</Label>
                        <Input value={process8.die_id} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly data-testid="p8-die-id" />
                        <p className="text-xs text-amber-400 mt-1">Auto-populated from Prod & Material Info</p>
                      </div>
                    )}
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Printing (Auto)</Label>
                      <Input value={process8.printing === "yes" ? "YES" : "NO"} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly data-testid="p8-printing" />
                      <p className="text-xs text-amber-400 mt-1">Auto-populated from Prod & Material Info</p>
                    </div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Color (Auto)</Label>
                      <Input value={process8.color || '-'} className="bg-[#111827] border-[#374151] text-gray-400 mt-1" readOnly data-testid="p8-color" />
                    </div>
                    {(!process8.die_ids || process8.die_ids.length === 0) && (
                      <div>
                        <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Counting</Label>
                        <Input type="number" value={process8.counting} onChange={(e) => setProcess8(prev => ({ ...prev, counting: e.target.value }))} className="bg-[#111827] border-[#374151] text-white mt-1" data-testid="p8-counting" />
                        {process3.sheet_counting && headerData.ups && (
                          <p className="text-xs text-amber-400 mt-1">
                            P3 Pasting ({process3.sheet_counting}) x UPS ({headerData.ups}) = <span className="font-bold">{parseInt(process3.sheet_counting || 0) * parseInt(String(headerData.ups).replace(/\D/g, '') || 1)}</span> Nos
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  {process8.die_ids?.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Die IDs & Counting (Auto-populated)</Label>
                      <p className="text-xs text-amber-400">Auto-populated from Prod & Material Info. Enter counting for each die separately.</p>
                      {process3.sheet_counting && headerData.ups && (
                        <p className="text-xs text-cyan-400">
                          Total expected: P3 Pasting ({process3.sheet_counting}) x UPS ({headerData.ups}) = <span className="font-bold">{parseInt(process3.sheet_counting || 0) * parseInt(String(headerData.ups).replace(/\D/g, '') || 1)}</span> Nos
                        </p>
                      )}
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        {process8.die_ids.map((did, idx) => (
                          <div key={did || idx} className="flex items-center gap-2 bg-[#0F172A] border border-[#374151] rounded-md p-2">
                            <span className="text-blue-400 font-mono text-sm min-w-[80px]">{did}</span>
                            <Input
                              type="number"
                              value={process8.die_countings?.[did] || ""}
                              onChange={(e) => setProcess8(prev => ({
                                ...prev,
                                die_countings: { ...prev.die_countings, [did]: e.target.value }
                              }))}
                              className="bg-[#111827] border-[#374151] text-white flex-1"
                              placeholder="Counting"
                              data-testid={`p8-counting-${did}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* PROCESS 9 */}
          {selectedProcesses.process9 && (
            <Card style={{ order: processOrder.indexOf('process9') >= 0 ? processOrder.indexOf('process9') : 99 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm transition-all">
              <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all py-3" onClick={() => toggleProcess('process9')}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="bg-[#2563EB] text-white px-3 py-1 rounded-md font-bold text-sm">P9</span>
                    <CardTitle className="text-lg text-[#F9FAFB] font-semibold">CALICO PASTING MACHINE</CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-[#111827] border border-[#374151] rounded-md px-3 py-1.5 hover:border-[#3B82F6] transition-all">
                      <Calendar className="h-4 w-4 text-[#9CA3AF]" />
                      <Input type="date" value={process9.date} onChange={(e) => { e.stopPropagation(); setProcess9(prev => ({ ...prev, date: e.target.value })); }} className="w-36 bg-transparent border-0 text-white p-0 h-auto focus-visible:ring-0 [color-scheme:dark]" placeholder="Select date" />
                    </div>
                    {expandedProcesses.process9 ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                  </div>
                </div>
              </CardHeader>
              {expandedProcesses.process9 && (
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Calico Color</Label>
                      <Select value={process9.calico_color} onValueChange={(val) => setProcess9(prev => ({ ...prev, calico_color: val }))}>
                        <SelectTrigger className="bg-[#111827] border-[#374151] text-white mt-1" data-testid="p9-calico-color"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['Blue', 'Black', 'Red', 'Green', 'Grey'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Number of Calico</Label>
                      <Select value={process9.number_of_calico} onValueChange={(val) => setProcess9(prev => ({ ...prev, number_of_calico: val }))}>
                        <SelectTrigger className="bg-[#111827] border-[#374151] text-white mt-1" data-testid="p9-number-calico"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['2', '4', '8', '12'].map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* PROCESS 9A - BOARD DRYING (Labor Cost Only) */}
          {selectedProcesses.process9a && (
            <Card style={{ order: processOrder.indexOf('process9a') >= 0 ? processOrder.indexOf('process9a') : 99 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm transition-all" data-testid="process9a-card">
              <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all py-3" onClick={() => toggleProcess('process9a')}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="bg-[#2563EB] text-white px-3 py-1 rounded-md font-bold text-sm">P9A</span>
                    <CardTitle className="text-lg text-[#F9FAFB] font-semibold">BOARD DRYING</CardTitle>
                    <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">Labor Cost Only</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-[#111827] border border-[#374151] rounded-md px-3 py-1.5 hover:border-[#3B82F6] transition-all">
                      <Calendar className="h-4 w-4 text-[#9CA3AF]" />
                      <Input type="date" value={process9a.date} onChange={(e) => { e.stopPropagation(); setProcess9a(prev => ({ ...prev, date: e.target.value })); }} className="w-36 bg-transparent border-0 text-white p-0 h-auto focus-visible:ring-0 [color-scheme:dark]" placeholder="Select date" data-testid="p9a-date" />
                    </div>
                    {expandedProcesses.process9a ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                  </div>
                </div>
              </CardHeader>
              {expandedProcesses.process9a && (
                <CardContent className="p-4">
                  <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4">
                    <p className="text-sm text-blue-400 font-medium mb-2">Clock-in / Clock-out process for labor cost analysis.</p>
                    <p className="text-xs text-[#9CA3AF]">No material is consumed or lost in this process. Employees will clock in and out to track labor time and cost.</p>
                  </div>
                  <div className="mt-3">
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Notes (Optional)</Label>
                    <Input 
                      value={process9a.notes} 
                      onChange={(e) => setProcess9a(prev => ({ ...prev, notes: e.target.value }))} 
                      className="bg-[#111827] border-[#374151] text-white mt-1" 
                      placeholder="Any drying notes..."
                      data-testid="p9a-notes"
                    />
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* PROCESS 10 - STITCHING & BUNDLING */}
          {selectedProcesses.process10 && (
            <Card style={{ order: processOrder.indexOf('process10') >= 0 ? processOrder.indexOf('process10') : 99 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm transition-all">
              <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all py-3" onClick={() => toggleProcess('process10')}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="bg-[#2563EB] text-white px-3 py-1 rounded-md font-bold text-sm">P10</span>
                    <CardTitle className="text-lg text-[#F9FAFB] font-semibold">STITCHING & BUNDLING MACHINE</CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-[#111827] border border-[#374151] rounded-md px-3 py-1.5 hover:border-[#3B82F6] transition-all">
                      <Calendar className="h-4 w-4 text-[#9CA3AF]" />
                      <Input type="date" value={process10.date} onChange={(e) => { e.stopPropagation(); setProcess10(prev => ({ ...prev, date: e.target.value })); }} className="w-36 bg-transparent border-0 text-white p-0 h-auto focus-visible:ring-0 [color-scheme:dark]" placeholder="Select date" />
                    </div>
                    {expandedProcesses.process10 ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                  </div>
                </div>
              </CardHeader>
              {expandedProcesses.process10 && (
                <CardContent className="p-4">
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">No Of Boxes in Bundle</Label>
                      <Input 
                        type="number" 
                        value={process10.boxes_in_bundle} 
                        onChange={(e) => setProcess10(prev => ({ ...prev, boxes_in_bundle: e.target.value }))} 
                        className="bg-[#111827] border-[#374151] text-white mt-1" 
                        data-testid="boxes-in-bundle-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Stitching Type</Label>
                      <Select value={process10.stitching_type} onValueChange={(val) => setProcess10(prev => ({ ...prev, stitching_type: val }))}>
                        <SelectTrigger className="bg-[#111827] border-[#374151] text-white mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Single">Single</SelectItem>
                          <SelectItem value="Double">Double</SelectItem>
                          <SelectItem value="Triple">Triple</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">No of Total Bundles</Label>
                      <Input 
                        type="number" 
                        value={process10.total_bundles} 
                        onChange={(e) => setProcess10(prev => ({ ...prev, total_bundles: e.target.value }))} 
                        className="bg-[#111827] border-[#374151] text-white mt-1" 
                        data-testid="total-bundles-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Total Number of Boxes</Label>
                      <div className="mt-1 px-4 py-2.5 bg-gradient-to-r from-amber-900/30 to-amber-800/20 border border-amber-500/50 rounded-md">
                        <span className="text-amber-300 font-bold text-xl" data-testid="total-boxes-display">
                          {totalBoxesCalculated || '0'}
                        </span>
                        <p className="text-xs text-amber-400/70 mt-0.5">
                          {process10.boxes_in_bundle && process10.total_bundles 
                            ? `${process10.boxes_in_bundle} × ${process10.total_bundles}` 
                            : 'Auto-calculated'}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* PROCESS 11 - LOADING / UNLOADING (Mandatory) */}
          <Card style={{ order: processOrder.indexOf('process11') >= 0 ? processOrder.indexOf('process11') : 98 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm transition-all">
            <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all py-3" onClick={() => toggleProcess('process11')}>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="bg-[#2563EB] text-white px-3 py-1 rounded-lg font-bold">P11</span>
                  <CardTitle className="text-xl text-[#F9FAFB]">LOADING / UNLOADING</CardTitle>
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">Mandatory</span>
                </div>
                <div className="flex items-center gap-4">
                  <Input 
                    type="date" 
                    value={process11.date} 
                    onChange={(e) => { e.stopPropagation(); setProcess11(prev => ({ ...prev, date: e.target.value })); }} 
                    className="w-40 bg-[#111827] border-[#374151] text-white [color-scheme:dark]" 
                  />
                  {expandedProcesses.process11 ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                </div>
              </div>
            </CardHeader>
            {expandedProcesses.process11 && (
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Loading Type</Label>
                    <Select value={process11.loading_type} onValueChange={(val) => setProcess11(prev => ({ ...prev, loading_type: val }))}>
                      <SelectTrigger className="bg-[#111827] border-[#374151] text-white mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Loading">Loading</SelectItem>
                        <SelectItem value="Unloading">Unloading</SelectItem>
                        <SelectItem value="Both">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Vehicle Number</Label>
                    <Input value={process11.vehicle_number} onChange={(e) => setProcess11(prev => ({ ...prev, vehicle_number: e.target.value }))} className="bg-[#111827] border-[#374151] text-white mt-1" placeholder="Enter vehicle number" />
                  </div>
                  <div>
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Remarks</Label>
                    <Input value={process11.remarks} onChange={(e) => setProcess11(prev => ({ ...prev, remarks: e.target.value }))} className="bg-[#111827] border-[#374151] text-white mt-1" placeholder="Any remarks" />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* PROCESS 12 - TRANSPORT (Mandatory) */}
          <Card style={{ order: processOrder.indexOf('process12') >= 0 ? processOrder.indexOf('process12') : 99 }} className="mb-4 bg-[#111827] border border-[#374151] border-l-4 border-l-[#3B82F6] shadow-sm transition-all">
            <CardHeader className="cursor-pointer bg-[#1F2937] hover:bg-[#2563EB]/10 transition-all py-3" onClick={() => toggleProcess('process12')}>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="bg-[#2563EB] text-white px-3 py-1 rounded-lg font-bold">P12</span>
                  <CardTitle className="text-xl text-[#F9FAFB]">TRANSPORT</CardTitle>
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">Mandatory</span>
                </div>
                <div className="flex items-center gap-4">
                  <Input 
                    type="date" 
                    value={process12.date} 
                    onChange={(e) => { e.stopPropagation(); setProcess12(prev => ({ ...prev, date: e.target.value })); }} 
                    className="w-40 bg-[#111827] border-[#374151] text-white [color-scheme:dark]" 
                  />
                  {expandedProcesses.process12 ? <ChevronUp className="text-[#9CA3AF]" /> : <ChevronDown className="text-[#9CA3AF]" />}
                </div>
              </div>
            </CardHeader>
            {expandedProcesses.process12 && (
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Transport Type</Label>
                    <Select value={process12.transport_type} onValueChange={(val) => setProcess12(prev => ({ ...prev, transport_type: val }))}>
                      <SelectTrigger className="bg-[#111827] border-[#374151] text-white mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Internal">Internal</SelectItem>
                        <SelectItem value="External">External</SelectItem>
                        <SelectItem value="Customer Pickup">Customer Pickup</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Destination</Label>
                    <Input value={process12.destination} onChange={(e) => setProcess12(prev => ({ ...prev, destination: e.target.value }))} className="bg-[#111827] border-[#374151] text-white mt-1" placeholder="Enter destination" />
                  </div>
                  <div>
                    <Label className="text-[#9CA3AF] font-semibold text-xs uppercase tracking-wider">Transport Remarks</Label>
                    <Input value={process12.transport_remarks} onChange={(e) => setProcess12(prev => ({ ...prev, transport_remarks: e.target.value }))} className="bg-[#111827] border-[#374151] text-white mt-1" placeholder="Any remarks" />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          </div>{/* End of flex process cards container */}

        </div>
      </div>

      {/* PRINT LAYOUT - Clean Production Floor Version - STANDARD DATA ONLY */}
      <div className="print-only">
        <style type="text/css">{`
          @media print {
            @page { size: A4; margin: 10mm; }
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
          .wo-print-page { font-family: Arial, sans-serif; color: #000; background: #fff; padding: 15px; }
          .wo-print-header { text-align: center; border-bottom: 3px solid #000; padding-bottom: 15px; margin-bottom: 15px; }
          .wo-print-header h1 { font-size: 22px; font-weight: bold; margin: 0; text-transform: uppercase; letter-spacing: 1px; }
          .wo-print-header h2 { font-size: 16px; color: #000; margin: 5px 0 0 0; }
          .wo-print-main-info { display: flex; justify-content: space-between; background: #fff; border: 2px solid #000; padding: 12px; margin-bottom: 15px; }
          .wo-print-main-info .wo-number { font-size: 28px; font-weight: bold; color: #000; }
          .wo-print-main-info .wo-details { text-align: right; }
          .wo-print-main-info .wo-details div { font-size: 12px; margin: 2px 0; }
          .wo-print-main-info .wo-details strong { color: #000; }
          .wo-print-box-info { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 15px; }
          .wo-print-box-item { background: #fff; border: 2px solid #000; padding: 10px; text-align: center; }
          .wo-print-box-item .label { font-size: 10px; color: #000; text-transform: uppercase; font-weight: bold; }
          .wo-print-box-item .value { font-size: 18px; font-weight: bold; color: #000; margin-top: 3px; }
          .wo-print-section { margin-bottom: 12px; page-break-inside: avoid; }
          .wo-print-section-header { background: #000; color: #fff; padding: 8px 12px; font-size: 13px; font-weight: bold; display: flex; justify-content: space-between; align-items: center; }
          .wo-print-section-header .process-name { text-transform: uppercase; }
          .wo-print-section-header .process-date { font-size: 11px; font-weight: normal; background: #fff; color: #000; padding: 2px 8px; }
          .wo-print-task-table { width: 100%; border-collapse: collapse; }
          .wo-print-task-table th { background: #f0f0f0; padding: 8px 10px; text-align: left; font-size: 11px; font-weight: bold; border: 1px solid #000; text-transform: uppercase; }
          .wo-print-task-table td { padding: 8px 10px; font-size: 12px; border: 1px solid #000; }
          .wo-print-task-table .task-label { font-weight: bold; background: #f5f5f5; width: 35%; }
          .wo-print-task-table .task-value { font-size: 14px; color: #000; font-weight: bold; }
          .wo-print-task-table .highlight-row { background: #eee; }
          .wo-print-materials { margin-top: 5px; }
          .wo-print-materials table { width: 100%; border-collapse: collapse; }
          .wo-print-materials th { background: #f0f0f0; padding: 6px 8px; font-size: 10px; text-transform: uppercase; border: 1px solid #000; text-align: center; font-weight: bold; }
          .wo-print-materials td { padding: 6px 8px; font-size: 11px; border: 1px solid #000; text-align: center; }
          .wo-print-footer { margin-top: 20px; display: flex; justify-content: space-between; border-top: 2px solid #000; padding-top: 15px; }
          .wo-print-footer .signature-box { width: 45%; }
          .wo-print-footer .signature-label { font-size: 11px; color: #000; margin-bottom: 5px; font-weight: bold; }
          .wo-print-footer .signature-line { border-bottom: 1px solid #000; height: 40px; }
          .wo-print-qty-badge { display: inline-block; background: #000; color: #fff; padding: 5px 15px; font-size: 16px; font-weight: bold; }
          .wo-print-size-box { flex: 1; background: #fff; border: 2px solid #000; padding: 10px; text-align: center; }
          .wo-print-size-label { font-size: 10px; color: #000; text-transform: uppercase; font-weight: bold; }
          .wo-print-size-value { font-size: 18px; font-weight: bold; color: #000; }
        `}</style>
        
        <div className="wo-print-page">
          {/* Header */}
          <div className="wo-print-header">
            <h1>Challenge Packaging Industries</h1>
            <h2>WORK ORDER - PRODUCTION SHEET</h2>
          </div>

          {/* Main Info Card */}
          <div className="wo-print-main-info">
            <div>
              <div className="wo-number">{headerData.work_order_number}</div>
              <div style={{fontSize: '14px', marginTop: '5px'}}><strong>Customer:</strong> {headerData.customer_name}</div>
              <div style={{fontSize: '14px'}}><strong>Item:</strong> {headerData.item_name}</div>
            </div>
            <div className="wo-details">
              <div><strong>Date:</strong> {headerData.work_order_creation_date}</div>
              <div><strong>Delivery:</strong> {headerData.delivery_date}</div>
              <div style={{marginTop: '8px'}}>
                <span className="wo-print-qty-badge">QTY: {headerData.required_quantity}</span>
              </div>
            </div>
          </div>

          {/* Box Dimensions */}
          <div className="wo-print-box-info">
            <div className="wo-print-box-item">
              <div className="label">Length</div>
              <div className="value">{headerData.length_mm || '-'} mm</div>
            </div>
            <div className="wo-print-box-item">
              <div className="label">Breadth</div>
              <div className="value">{headerData.breadth_mm || '-'} mm</div>
            </div>
            <div className="wo-print-box-item">
              <div className="label">Height</div>
              <div className="value">{headerData.height_mm || '-'} mm</div>
            </div>
            <div className="wo-print-box-item">
              <div className="label">PLY</div>
              <div className="value">{process3.no_of_ply || '-'}</div>
            </div>
          </div>

          {/* Reel Size & Cutting Size */}
          <div style={{display: 'flex', gap: '10px', marginBottom: '15px'}}>
            <div className="wo-print-size-box">
              <div className="wo-print-size-label">Reel Size</div>
              <div className="wo-print-size-value">{headerData.reel_size || '-'}"</div>
            </div>
            <div className="wo-print-size-box">
              <div className="wo-print-size-label">Cutting Size</div>
              <div className="wo-print-size-value">{headerData.cutting_size || '-'}"</div>
            </div>
          </div>

          {/* Process 1 - Corrugation */}
          {selectedProcesses.process1 && (
            <div className="wo-print-section">
              <div className="wo-print-section-header">
                <span className="process-name">P1 - CORRUGATION</span>
                <span className="process-date">Date: {process1.date || '-'}</span>
              </div>
              <table className="wo-print-task-table">
                <tbody>
                  <tr className="highlight-row">
                    <td className="task-label">Flute Type</td>
                    <td className="task-value">{process1.flute_type || '-'}</td>
                  </tr>
                </tbody>
              </table>
              {process1.rows && process1.rows.filter(r => r.base_reel_id || r.flute_reel_id).length > 0 && (
                <div className="wo-print-materials">
                  <table>
                    <thead>
                      <tr>
                        <th>Base Reel ID</th>
                        <th>Base Weight (kg)</th>
                        <th>Flute Reel ID</th>
                        <th>Flute Weight (kg)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {process1.rows.filter(r => r.base_reel_id || r.flute_reel_id).map((row, i) => (
                        <tr key={`print-p1-${row.base_reel_id || ''}-${row.flute_reel_id || ''}-${i}`}>
                          <td style={{fontWeight: 'bold'}}>{row.base_reel_id || '-'}</td>
                          <td>{row.base_weight || '-'}</td>
                          <td style={{fontWeight: 'bold'}}>{row.flute_reel_id || '-'}</td>
                          <td>{row.flute_weight || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Process 2A - Sheet Cutting */}
          {selectedProcesses.process2a && (
            <div className="wo-print-section">
              <div className="wo-print-section-header">
                <span className="process-name">P2A - SHEET CUTTING</span>
                <span className="process-date">Date: {process2a.date || '-'}</span>
              </div>
              <table className="wo-print-task-table">
                <tbody>
                  <tr>
                    <td className="task-label">Cutting Size</td>
                    <td className="task-value">{process2a.cutting_size || '-'}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Sheet Count</td>
                    <td className="task-value">{process2a.sheet_counting || '-'}</td>
                  </tr>
                </tbody>
              </table>
              {process2a.reels && process2a.reels.filter(r => r.reel_no).length > 0 && (
                <div className="wo-print-materials">
                  <table>
                    <thead>
                      <tr>
                        <th>Reel No</th>
                        <th>Size</th>
                        <th>Weight (kg)</th>
                        <th>Product</th>
                      </tr>
                    </thead>
                    <tbody>
                      {process2a.reels.filter(r => r.reel_no).map((reel, i) => (
                        <tr key={`print-p2a-${reel.reel_no || i}`}>
                          <td style={{fontWeight: 'bold'}}>{reel.reel_no}</td>
                          <td>{reel.reel_size || '-'}</td>
                          <td>{reel.reel_weight || '-'}</td>
                          <td>{reel.reel_product || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Process 2B - Paper Cutting */}
          {selectedProcesses.process2b && (
            <div className="wo-print-section">
              <div className="wo-print-section-header">
                <span className="process-name">P2B - PAPER CUTTING</span>
                <span className="process-date">Date: {process2b.date || '-'}</span>
              </div>
              <table className="wo-print-task-table">
                <tbody>
                  <tr>
                    <td className="task-label">Cutting Size</td>
                    <td className="task-value">{process2b.cutting_size || '-'}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Paper Count</td>
                    <td className="task-value">{process2b.paper_counting || '-'}</td>
                  </tr>
                </tbody>
              </table>
              {process2b.reels && process2b.reels.filter(r => r.reel_no).length > 0 && (
                <div className="wo-print-materials">
                  <table>
                    <thead>
                      <tr>
                        <th>Reel No</th>
                        <th>Size</th>
                        <th>Weight (kg)</th>
                        <th>Product</th>
                      </tr>
                    </thead>
                    <tbody>
                      {process2b.reels.filter(r => r.reel_no).map((reel, i) => (
                        <tr key={`p2b-${reel.reel_no || i}`}>
                          <td style={{fontWeight: 'bold'}}>{reel.reel_no}</td>
                          <td>{reel.reel_size || '-'}</td>
                          <td>{reel.reel_weight || '-'}</td>
                          <td>{reel.reel_product || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Process 3 - Sheet Pasting */}
          {selectedProcesses.process3 && (
            <div className="wo-print-section">
              <div className="wo-print-section-header">
                <span className="process-name">P3 - SHEET PASTING</span>
                <span className="process-date">Date: {process3.date || '-'}</span>
              </div>
              <table className="wo-print-task-table">
                <tbody>
                  <tr className="highlight-row">
                    <td className="task-label">PLY</td>
                    <td className="task-value">{process3.no_of_ply || '-'}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Top Paper Type</td>
                    <td className="task-value">{process3.top_paper_type || '-'}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Reel Size</td>
                    <td className="task-value">{process3.reel_size || '-'}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Cutting Size</td>
                    <td className="task-value">{process3.cutting_size || '-'}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Sheet Counting</td>
                    <td className="task-value">{process3.sheet_counting || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Process 4 - Slitter Scorer */}
          {selectedProcesses.process4 && (
            <div className="wo-print-section">
              <div className="wo-print-section-header">
                <span className="process-name">P4 - SLITTER SCORER</span>
                <span className="process-date">Date: {process4.date || '-'}</span>
              </div>
              <table className="wo-print-task-table">
                <tbody>
                  <tr>
                    <td className="task-label">Length</td>
                    <td className="task-value">{process4.length || '-'}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Breadth</td>
                    <td className="task-value">{process4.breadth || '-'}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Height</td>
                    <td className="task-value">{process4.height || '-'}</td>
                  </tr>
                  <tr className="highlight-row">
                    <td className="task-label">Board Counting</td>
                    <td className="task-value">{process4.board_counting || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Process 5 - Rotary Creasing */}
          {selectedProcesses.process5 && (
            <div className="wo-print-section">
              <div className="wo-print-section-header">
                <span className="process-name">P5 - ROTARY CREASING</span>
                <span className="process-date">Date: {process5.date || '-'}</span>
              </div>
              <table className="wo-print-task-table">
                <tbody>
                  <tr>
                    <td className="task-label">Printing</td>
                    <td className="task-value">{process5.printing || '-'}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Color</td>
                    <td className="task-value">{process5.color || '-'}</td>
                  </tr>
                  <tr className="highlight-row">
                    <td className="task-label">Board Counting</td>
                    <td className="task-value">{process5.board_counting || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Process 6 - RS4 Printing & Slotting */}
          {selectedProcesses.process6 && (
            <div className="wo-print-section">
              <div className="wo-print-section-header">
                <span className="process-name">P6 - RS4 PRINTING & SLOTTING</span>
                <span className="process-date">Date: {process6.date || '-'}</span>
              </div>
              <table className="wo-print-task-table">
                <tbody>
                  <tr>
                    <td className="task-label">Length</td>
                    <td className="task-value">{process6.length || '-'}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Breadth</td>
                    <td className="task-value">{process6.breadth || '-'}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Height</td>
                    <td className="task-value">{process6.height || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Process 7 - Flat Bed Punching */}
          {selectedProcesses.process7 && (
            <div className="wo-print-section">
              <div className="wo-print-section-header">
                <span className="process-name">P7 - FLAT BED PUNCHING</span>
                <span className="process-date">Date: {process7.date || '-'}</span>
              </div>
              <table className="wo-print-task-table">
                <tbody>
                  <tr className="highlight-row">
                    <td className="task-label">Die ID(s)</td>
                    <td className="task-value">{process7.die_ids?.length > 0 ? process7.die_ids.join(', ') : (process7.die_id || '-')}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Number of Dies</td>
                    <td className="task-value">{process7.die_ids?.length > 0 ? process7.die_ids.length : (process7.number_of_dies || '1')}</td>
                  </tr>
                  {process7.die_ids?.length > 0 ? (
                    process7.die_ids.map((did) => (
                      <tr key={did}>
                        <td className="task-label">Counting ({did})</td>
                        <td className="task-value">{process7.die_countings?.[did] || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="task-label">Counting</td>
                      <td className="task-value">{process7.counting || '-'}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Process 8 - Rotary Die Punching */}
          {selectedProcesses.process8 && (
            <div className="wo-print-section">
              <div className="wo-print-section-header">
                <span className="process-name">P8 - ROTARY DIE PUNCHING</span>
                <span className="process-date">Date: {process8.date || '-'}</span>
              </div>
              <table className="wo-print-task-table">
                <tbody>
                  <tr className="highlight-row">
                    <td className="task-label">Die ID(s)</td>
                    <td className="task-value">{process8.die_ids?.length > 0 ? process8.die_ids.join(', ') : (process8.die_id || '-')}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Printing</td>
                    <td className="task-value">{process8.printing || '-'}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Color</td>
                    <td className="task-value">{process8.color || '-'}</td>
                  </tr>
                  {process8.die_ids?.length > 0 ? (
                    process8.die_ids.map((did) => (
                      <tr key={did}>
                        <td className="task-label">Counting ({did})</td>
                        <td className="task-value">{process8.die_countings?.[did] || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="task-label">Counting</td>
                      <td className="task-value">{process8.counting || '-'}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Process 9 - Calico Pasting */}
          {selectedProcesses.process9 && (
            <div className="wo-print-section">
              <div className="wo-print-section-header">
                <span className="process-name">P9 - CALICO PASTING</span>
                <span className="process-date">Date: {process9.date || '-'}</span>
              </div>
              <table className="wo-print-task-table">
                <tbody>
                  <tr>
                    <td className="task-label">Calico Color</td>
                    <td className="task-value">{process9.calico_color || '-'}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Number of Calico</td>
                    <td className="task-value">{process9.number_of_calico || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Process 9A - Board Drying */}
          {selectedProcesses.process9a && (
            <div className="wo-print-section">
              <div className="wo-print-section-header">
                <span className="process-name">P9A - BOARD DRYING</span>
                <span className="process-date">Date: {process9a.date || '-'}</span>
              </div>
              <table className="wo-print-task-table">
                <tbody>
                  <tr>
                    <td className="task-label">Type</td>
                    <td className="task-value">Clock-in / Clock-out (Labor Cost Only)</td>
                  </tr>
                  {process9a.notes && (
                    <tr>
                      <td className="task-label">Notes</td>
                      <td className="task-value">{process9a.notes}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Process 10 - Stitching & Bundling */}
          {selectedProcesses.process10 && (
            <div className="wo-print-section">
              <div className="wo-print-section-header">
                <span className="process-name">P10 - STITCHING & BUNDLING</span>
                <span className="process-date">Date: {process10.date || '-'}</span>
              </div>
              <table className="wo-print-task-table">
                <tbody>
                  <tr>
                    <td className="task-label">Boxes in Bundle</td>
                    <td className="task-value">{process10.boxes_in_bundle || '-'}</td>
                  </tr>
                  <tr>
                    <td className="task-label">Stitching Type</td>
                    <td className="task-value">{process10.stitching_type || '-'}</td>
                  </tr>
                  <tr className="highlight-row">
                    <td className="task-label">Total Bundles</td>
                    <td className="task-value">{process10.total_bundles || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Footer with Signatures */}
          <div className="wo-print-footer">
            <div className="signature-box">
              <div className="signature-label">Prepared By</div>
              <div className="signature-line"></div>
            </div>
            <div className="signature-box">
              <div className="signature-label">Approved By</div>
              <div className="signature-line"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Used Reel Warning Dialog */}
      <AlertDialog open={usedReelWarning.open} onOpenChange={(open) => setUsedReelWarning(prev => ({ ...prev, open }))}>
        <AlertDialogContent className="bg-[#111827] border-orange-500/50 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F9FAFB] font-semibold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Stock Already Used
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              <div className="space-y-3">
                <p>
                  The reel <span className="font-bold text-[#9CA3AF]">{usedReelWarning.reelId}</span> has already been used in the following work order(s):
                </p>
                <div className="flex flex-wrap gap-2">
                  {usedReelWarning.workOrders.map((wo, idx) => (
                    <span key={idx} className="px-3 py-1 bg-orange-500/20 text-[#9CA3AF] rounded-full text-sm font-medium">
                      {wo}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-gray-400 mt-3">
                  Do you still want to use this reel? If yes, this work order will be added to the usage history.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-700 text-white hover:bg-gray-600 border-gray-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                if (usedReelWarning.callback) {
                  usedReelWarning.callback();
                }
                setUsedReelWarning({ open: false, reelId: "", workOrders: [], callback: null, type: "", index: null });
              }}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Yes, Use This Reel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reel Suggestion Dialog — opens after Product pick and via manual P1 button */}
      <ReelSuggestionDialog
        open={reelSuggestionOpen}
        onClose={() => setReelSuggestionOpen(false)}
        spec={reelSuggestionSpec}
        availableInventory={availableInventory}
        onApply={handleReelSuggestionApply}
      />
    </>
  );
};

export default WorkOrderCreationNew;
